
// ...existing code...
import { useContext, useEffect, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { RadioButton } from "primereact/radiobutton";
import { Toast } from "primereact/toast";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { RouterAddress } from "../../../constants/RouterAddress";
import { ROLE_TYPE_ADMIN, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { getErrorTitle, USER_GROUP_ADMIN } from "../../../constants/constants";
import { openUrlInNewTab } from "../../../utils/securityUtils";
import {
    dashboardPageContainerStyle,
} from "../../dashboard/pages/dashboardStyles";
import {
    appAccentPanelStyle,
    appMutedTextStyle,
    appSectionCardStyle,
} from "../../../components/general/uiSurfaceStyles";
import { getDelegationFormConfig } from "./delegationFormRegistry";
import {
    DELEGATION_FORM_STATUS,
    type DelegationFormStatus,
    normalizeDelegationStatus,
} from "./delegationStatus";
import {
    createEmptyLtcwCompetencyEntry,
    createEmptyRescindingDelegationRow,
    createEmptyPrnMedicationOrder,
    createDefaultStandardDelegationForm,
    getDelegationTemplateDefinition,
    getLatestDelegationTemplateVersion,
    getDelegationInstructions,
    getDelegationInstructionsTitle,
    getDelegationFormNotes,
    getDelegationPrintFooter,
    getDelegationPrintHeader,
    getDelegationPrintHint,
    type StandardDelegationFormDefinition,
    type StandardDelegationFieldConfig,
} from "./delegationFormTemplates";
import { formatDelegationTaskSteps } from "../utils/delegationTaskFormatting";
import {
    ensureResidentBinderBundle,
    getBinderFolderByCode,
    RESIDENT_BINDER_FOLDER_CODES,
    upsertBinderDocumentWithVersion,
} from "../../../utils/binderFeatureUtils";

const OFFICE_OPTIONS = ["HCS", "AAA", "DDA", "Other"];
const METHOD_OPTIONS = ["E-mail", "Telephone", "Fax"];
const ATTACHMENT_OPTIONS = [
    "Care / DDA Assessment",
    "PCSP / DDA",
    "PBSP",
    "Service Summary Plan",
    "Consent (DSHS 14-012)",
];
const RESCINDING_DELEGATION_FORM_CODE = "13-680";
const RESCINDING_DELEGATION_MAX_ROWS = 10;
const NURSING_VISIT_FORM_CODE = "14-484";

const createDefaultForm = (residentName = "") => ({
    offices: [] as string[],
    officeOther: "",
    authorizationNumber: "",
    rnProviderOneId: "",
    dateOfReferral: "",
    methods: [] as string[],
    nurseAgency: "",
    nursePhone: "",
    nurseFax: "",
    nurseEmail: "",
    crmNameOffice: "",
    crmEmail: "",
    crmPhone: "",
    crmFax: "",
    attachments: [] as string[],
    clientName: residentName,
    guardianName: "",
    acesId: "",
    clientDob: "",
    clientPhone: "",
    addressLine1: "",
    city: "",
    stateZip: "WA ",
    providerName: "",
    providerPhone: "",
    providerFax: "",
    clientGuardianEmail: "",
    needsInterpreter: false,
    primaryLanguage: "",
    deafHoh: false,
    primaryDiagnosis: "",
    referralReason: "",
    crmSignature: "",
    crmSignatureDate: "",
    receiptDate: "",
    referralAccepted: "accepted",
    assignedNurse: "",
    receiptSignature: "",
    receiptContact: "",
    additionalComments: "",
    responseCrmName: "",
    responseCrmEmail: "",
    responseCrmPhone: "",
    rndName: "",
    rndProviderOneId: "",
    rndEmail: "",
    rndPhone: "",
    rndFax: "",
    delegationStarted: "yes",
    assessmentDate: "",
    delegatedTasks: "",
    notImplementedReason: "",
    careOptions: "",
    rndComments: "",
    rndSignature: "",
    rndSignatureDate: "",
});


const fieldLabelStyle = {
    display: "block",
    fontWeight: 700,
    color: "#334155",
    marginBottom: "0.5rem",
    fontSize: "0.9rem",
    lineHeight: 1.35,
};

const fieldShellStyle = {
    border: "1px solid #dbe7f3",
    borderRadius: 16,
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    padding: "0.8rem 0.9rem",
    height: "100%",
};

const calendarInputStyle = {
    width: "100%",
};

const calendarFieldStyle = {
    width: "14rem",
    maxWidth: "100%",
};

const optionGroupStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "0.75rem",
};

const optionChipStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "0.75rem",
    width: "100%",
    minHeight: "100%",
    padding: "0.85rem 0.95rem",
    borderRadius: 14,
    border: "1px solid #dbe7f3",
    background: "#ffffff",
    boxShadow: "0 6px 16px rgba(15, 23, 42, 0.04)",
    color: "#334155",
    fontWeight: 600,
    cursor: "pointer",
};

const optionCheckboxWrapStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "1.25rem",
    minWidth: "1.25rem",
    paddingTop: "0.1rem",
};

const pageShellStyle = {
    padding: "1.5rem 1rem 3.5rem",
    background: "linear-gradient(180deg, #f8fbff 0%, #f4f7fb 38%, #eef3f8 100%)",
    minHeight: "100%",
};

const centeredContentStyle = {
    width: "100%",
    maxWidth: "1180px",
    margin: "0 auto",
};

const PRN_MEDICATION_FORM_CODE = "13-678A";

const normalizePrnOrders = (rawData: any) => {
    if (Array.isArray(rawData?.prnOrders) && rawData.prnOrders.length > 0) {
        return rawData.prnOrders.map((order: any) => ({
            ...createEmptyPrnMedicationOrder(),
            ...(order || {}),
        }));
    }

    const legacyOrders = [
        {
            dateOrdered: rawData?.orderOneDateOrdered || "",
            medicationName: rawData?.orderOneMedicationName || "",
            doseFrequency: rawData?.orderOneDoseFrequency || "",
            route: rawData?.orderOneRoute || "",
            notToExceed: rawData?.orderOneNotToExceed || "",
            reason: rawData?.orderOneReason || "",
            symptomsAmount: rawData?.orderOneSymptomsAmount || "",
            notes: rawData?.orderOneNotes || "",
        },
        {
            dateOrdered: rawData?.orderTwoDateOrdered || "",
            medicationName: rawData?.orderTwoMedicationName || "",
            doseFrequency: rawData?.orderTwoDoseFrequency || "",
            route: rawData?.orderTwoRoute || "",
            notToExceed: rawData?.orderTwoNotToExceed || "",
            reason: rawData?.orderTwoReason || "",
            symptomsAmount: rawData?.orderTwoSymptomsAmount || "",
            notes: rawData?.orderTwoNotes || "",
        },
    ].filter((order) => Object.values(order).some((value) => String(value || "").trim()));

    return legacyOrders.length > 0 ? legacyOrders : [createEmptyPrnMedicationOrder()];
};

const normalizeRescindingDelegationRows = (rawData: any) => {
    if (Array.isArray(rawData?.rescindedEntries) && rawData.rescindedEntries.length > 0) {
        const normalizedRows = rawData.rescindedEntries
            .map((entry: any) => ({
                ...createEmptyRescindingDelegationRow(),
                ...(entry || {}),
            }))
            .filter((entry: any) => (
                String(entry?.caregiverName || "").trim()
                || String(entry?.medicationOrTreatment || "").trim()
                || String(entry?.notes || "").trim()
            ));

        return normalizedRows.length > 0
            ? normalizedRows.slice(0, RESCINDING_DELEGATION_MAX_ROWS)
            : [createEmptyRescindingDelegationRow()];
    }

    const caregiverLines = String(rawData?.caregiverNames || "").split(/\r?\n|;/).map((item) => item.trim()).filter(Boolean);
    const medicationLines = String(rawData?.rescindedTasks || "").split(/\r?\n|;/).map((item) => item.trim()).filter(Boolean);
    const noteLines = String(rawData?.notes || "").split(/\r?\n|;/).map((item) => item.trim()).filter(Boolean);
    const rowCount = Math.max(caregiverLines.length, medicationLines.length, noteLines.length);

    if (!rowCount) {
        return [createEmptyRescindingDelegationRow()];
    }

    return Array.from({ length: Math.min(rowCount, RESCINDING_DELEGATION_MAX_ROWS) }, (_item, index) => ({
        caregiverName: caregiverLines[index] || "",
        medicationOrTreatment: medicationLines[index] || "",
        notes: noteLines[index] || "",
    }));
};

const normalizeLtcwCompetencyEntries = (rawData: any) => {
    if (Array.isArray(rawData?.ltcwCompetencyEntries) && rawData.ltcwCompetencyEntries.length > 0) {
        const normalizedRows = rawData.ltcwCompetencyEntries
            .map((entry: any) => ({
                ...createEmptyLtcwCompetencyEntry(),
                ...(entry || {}),
                activeCredential: String(entry?.activeCredential || "yes").trim().toLowerCase() === "no" ? "no" : "yes",
            }))
            .filter((entry: any) => (
                String(entry?.ltcwName || "").trim()
                || entry?.observationDemonstration
                || entry?.verbalDescription
                || entry?.recordReview
                || String(entry?.trainingNeededDate || "").trim()
                || String(entry?.trainingCompletedDate || "").trim()
                || String(entry?.otherSpecify || "").trim()
            ));

        return normalizedRows.length > 0 ? normalizedRows : [createEmptyLtcwCompetencyEntry()];
    }

    const legacySummary = String(rawData?.ltcwCompetencySummary || "").trim();
    if (!legacySummary) {
        return [createEmptyLtcwCompetencyEntry()];
    }

    return [{
        ...createEmptyLtcwCompetencyEntry(),
        otherSpecify: legacySummary,
    }];
};

const normalizeDelegationFormData = (formCode: string, rawData: any, residentName = "") => {
    const resolvedTemplateVersion = String(rawData?.templateVersion || "").trim() || undefined;
    const resolvedTemplateSnapshot = rawData?.templateDefinition as StandardDelegationFormDefinition | undefined;
    const baseData = {
        ...(formCode === "01-212"
            ? createDefaultForm(residentName)
            : createDefaultStandardDelegationForm(residentName, "", formCode, resolvedTemplateVersion, resolvedTemplateSnapshot)),
        ...(rawData || {}),
    };

    if (formCode === PRN_MEDICATION_FORM_CODE) {
        return {
            ...baseData,
            prnOrders: normalizePrnOrders(rawData),
        };
    }

    if (formCode === RESCINDING_DELEGATION_FORM_CODE) {
        return {
            ...baseData,
            rescindedEntries: normalizeRescindingDelegationRows(rawData),
        };
    }

    if (formCode === NURSING_VISIT_FORM_CODE) {
        const normalizedAdditionalPageUsed = typeof baseData?.additionalPageUsed === "boolean"
            ? baseData.additionalPageUsed
            : String(baseData?.additionalPageUsed || "").trim().toLowerCase() === "true";

        return {
            ...baseData,
            additionalPageUsed: normalizedAdditionalPageUsed,
            ltcwCompetencyEntries: normalizeLtcwCompetencyEntries(rawData),
        };
    }

    if (formCode === "01-212") {
        const normalizedStateZip =
            String(baseData?.stateZip || "").trim()
            || [String(baseData?.state || "").trim(), String(baseData?.zipCode || "").trim()].filter(Boolean).join(" ").trim();
        const normalizedReceiptContact =
            String(baseData?.receiptContact || "").trim()
            || [String(baseData?.receiptPhone || "").trim(), String(baseData?.receiptEmail || "").trim()].filter(Boolean).join(" / ");

        return {
            ...baseData,
            stateZip: normalizedStateZip || "WA",
            receiptContact: normalizedReceiptContact,
            clientName: String(baseData?.clientName || "").trim() || residentName,
            providerFax: String(baseData?.providerFax || "").trim(),
            additionalComments: String(baseData?.additionalComments || "").trim(),
        };
    }

    if (formCode === "13-893") {
        return {
            ...baseData,
            additionalUnitsNeeded: String(baseData?.additionalUnitsNeeded || "").trim(),
            requestMonth: String(baseData?.requestMonth || baseData?.authorizedMonth || "").trim(),
            totalUnitsForMonth: String(baseData?.totalUnitsForMonth || baseData?.totalUnitsRequested || "").trim(),
            requestComments: String(baseData?.requestComments || baseData?.additionalUnitsReason || "").trim(),
        };
    }

    if (formCode === "10-217") {
        const normalizeCredentialValue = (value: any) => String(value || "").trim();
        const normalizedCredentialType = Array.isArray(baseData?.credentialType)
            ? baseData.credentialType
            : normalizeCredentialValue(baseData?.credentialType)
                ? [normalizeCredentialValue(baseData.credentialType)]
                : [];

        return {
            ...baseData,
            credentialType: normalizedCredentialType,
        };
    }

    if (formCode === "13-678") {
        const normalizedMedicationAttachmentIncluded = Boolean(
            baseData?.delegatedMedicationAttachmentIncluded
            || String(baseData?.delegatedMedicationDetails || "").trim(),
        );
        const normalizedTeachingAidesAttached = Boolean(
            baseData?.additionalTeachingAidesAttached
            || String(baseData?.additionalTeachingAides || "").trim(),
        );

        return {
            ...baseData,
            delegatedMedicationAttachmentIncluded: normalizedMedicationAttachmentIncluded,
            additionalTeachingAidesAttached: normalizedTeachingAidesAttached,
        };
    }

    if (formCode === "13-681") {
        const normalizedMedicationChangeType = Array.isArray(baseData?.medicationChangeType)
            ? baseData.medicationChangeType
            : String(baseData?.medicationChangeType || "").trim()
                ? [String(baseData.medicationChangeType).trim().toLowerCase() === "changed" ? "Changed" : "New"]
                : [];
        const normalizedDelegationDecision = Array.isArray(baseData?.delegationDecision)
            ? baseData.delegationDecision
            : String(baseData?.delegationDecision || "").trim()
                ? [
                    String(baseData.delegationDecision).trim().toLowerCase() === "sitevisitrequired"
                        ? "A site visit is required for training or assessment prior to delegation."
                        : "Delegate immediately. No site visit required.",
                ]
                : [];

        return {
            ...baseData,
            medicationChangeType: normalizedMedicationChangeType,
            delegationDecision: normalizedDelegationDecision,
        };
    }

    return baseData;
};

const getDelegationThreadId = (form: any) => String(form?.formThreadId || "").trim() || "default";
const getDelegationThreadLabel = (form: any) => String(form?.formThreadLabel || "").trim();
const normalizeDelegationTitleToken = (value: any) => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
const buildDelegationDisplayTitle = (baseTitle: string, formThreadLabel?: string, staffName?: string) => {
    const normalizedBaseTitle = String(baseTitle || "Delegation Form").trim();
    const normalizedStaffName = String(staffName || "").trim();
    const titleWithStaff = normalizedStaffName
        && !normalizeDelegationTitleToken(normalizedBaseTitle).includes(normalizeDelegationTitleToken(normalizedStaffName))
        ? `${normalizedBaseTitle} - ${normalizedStaffName}`
        : normalizedBaseTitle;
    const normalizedSuffix = String(formThreadLabel || "").trim();
    return normalizedSuffix && !normalizeDelegationTitleToken(titleWithStaff).includes(normalizeDelegationTitleToken(normalizedSuffix))
        ? `${titleWithStaff} - ${normalizedSuffix}`
        : titleWithStaff;
};
const sanitizeDelegationFileSegment = (value: string) => String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

const resolveStandardFormTemplateState = (
    formCode: string,
    savedTemplateVersion?: string | null,
    savedTemplateDefinition?: StandardDelegationFormDefinition | null,
) => {
    const normalizedFormCode = String(formCode || "").trim().toUpperCase();
    if (normalizedFormCode === "01-212") {
        return {
            templateVersion: String(savedTemplateVersion || "").trim(),
            templateDefinition: (savedTemplateDefinition || null) as StandardDelegationFormDefinition | null,
            templateSchemaVersion: Number(savedTemplateDefinition?.schemaVersion || 1),
        };
    }

    const latestTemplateVersion = getLatestDelegationTemplateVersion(normalizedFormCode);
    const latestTemplateDefinition = getDelegationTemplateDefinition(normalizedFormCode, latestTemplateVersion, null);
    return {
        templateVersion: latestTemplateVersion,
        templateDefinition: null,
        templateSchemaVersion: Number(latestTemplateDefinition?.schemaVersion || 1),
    };
};

const sectionTitleStyle = {
    fontSize: "1.08rem",
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: "0.3rem",
    letterSpacing: "0.01em",
};

const sectionSubtitleStyle = {
    color: "#64748b",
    fontSize: "0.93rem",
    lineHeight: 1.5,
    marginBottom: "1.35rem",
};

const formCardStyle = {
    ...appSectionCardStyle,
    padding: "0.55rem 0.75rem 0.8rem",
    borderRadius: 20,
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.07)",
    border: "1px solid rgba(148, 163, 184, 0.22)",
    background: "rgba(255,255,255,0.98)",
};

const summaryGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "0.9rem",
    marginTop: "1rem",
};

const summaryTileStyle = {
    borderRadius: 16,
    border: "1px solid #dbe7f3",
    background: "#ffffff",
    padding: "0.95rem 1rem",
};

const summaryLabelStyle = {
    fontSize: "0.74rem",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    color: "#64748b",
    marginBottom: "0.35rem",
};

const summaryValueStyle = {
    fontSize: "1rem",
    fontWeight: 700,
    color: "#0f172a",
};

const layoutShellStyle = {
    display: "block",
};

const timelineNavStyle = {
    position: "sticky" as const,
    top: "1rem",
    zIndex: 5,
    borderRadius: 20,
    border: "1px solid rgba(148, 163, 184, 0.22)",
    background: "rgba(255,255,255,0.96)",
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
    padding: "0.95rem 1rem",
    marginBottom: "1rem",
};

const timelineTitleStyle = {
    fontSize: "0.78rem",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    color: "#64748b",
    marginBottom: "0.9rem",
};

const timelineListStyle = {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "0.35rem",
};

const timelineButtonStyle = {
    flex: "1 1 14rem",
    textAlign: "left" as const,
    border: "1px solid #dbe7f3",
    background: "#ffffff",
    padding: "0.7rem 0.8rem",
    display: "grid",
    gridTemplateColumns: "1.2rem minmax(0, 1fr)",
    gap: "0.75rem",
    alignItems: "start",
    cursor: "pointer",
    color: "#0f172a",
    borderRadius: "14px",
};

const floatLabelCss = `
    .delegation-referral-form .p-float-label {
        display: block;
        width: 100%;
    }

    .delegation-referral-form .p-float-label label {
        max-width: calc(100% - 1.5rem);
        white-space: normal;
        line-height: 1.15;
    }

    .delegation-referral-form .referral-form-card {
        overflow: hidden;
    }

    .delegation-referral-form .referral-form-card .p-card-body,
    .delegation-referral-form .referral-form-card .p-card-content {
        padding: 0;
    }

    .delegation-referral-form .form-section-grid {
        row-gap: 0.2rem;
    }

    .delegation-referral-form .form-section-grid > [class*="col-"] {
        padding-top: 0.55rem;
        padding-bottom: 0.55rem;
    }

    .delegation-referral-form .p-inputtext,
    .delegation-referral-form .p-inputtextarea,
    .delegation-referral-form .p-calendar .p-inputtext {
        border-radius: 12px;
        border-color: #cbd5e1;
        background: #ffffff;
        box-shadow: none;
    }

    .delegation-referral-form .p-inputtext:enabled:hover,
    .delegation-referral-form .p-inputtextarea:enabled:hover,
    .delegation-referral-form .p-calendar .p-inputtext:enabled:hover {
        border-color: #94a3b8;
    }

    .delegation-referral-form .p-inputtext:enabled:focus,
    .delegation-referral-form .p-inputtextarea:enabled:focus,
    .delegation-referral-form .p-calendar .p-inputtext:enabled:focus {
        border-color: #0ea5e9;
        box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.14);
        outline: none;
    }

    .delegation-referral-form .p-calendar,
    .delegation-referral-form .p-inputtextarea,
    .delegation-referral-form .p-inputtext {
        width: 100%;
    }

    .delegation-referral-form .p-checkbox,
    .delegation-referral-form .p-radiobutton {
        flex-shrink: 0;
        margin-top: 0.15rem;
    }

    .delegation-referral-form .timeline-dot {
        width: 1rem;
        height: 1rem;
        border-radius: 999px;
        background: #ffffff;
        border: 2px solid #0ea5e9;
        margin-top: 0.12rem;
    }

    .delegation-referral-form .timeline-step-label {
        font-size: 0.92rem;
        font-weight: 700;
        color: #0f172a;
        line-height: 1.35;
    }

    .delegation-referral-form .timeline-step-copy {
        font-size: 0.82rem;
        color: #64748b;
        line-height: 1.45;
        margin-top: 0.14rem;
    }

    @media print {
        body {
            background: #ffffff !important;
        }

        .delegation-referral-form {
            background: #ffffff !important;
            padding: 0 !important;
        }

        .delegation-referral-form .no-print,
        .delegation-referral-form .p-toast,
        .delegation-referral-form .p-dialog-mask,
        .delegation-referral-form .referral-timeline-nav {
            display: none !important;
        }

        .app-header-shell,
        .app-breadcrumbs-shell,
        footer.footerContainer {
            display: none !important;
        }

        .delegation-referral-form .print-shell,
        .delegation-referral-form .print-sheet {
            max-width: none !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            background: #ffffff !important;
        }

        .delegation-referral-form .print-sheet,
        .delegation-referral-form .referral-form-card,
        .delegation-referral-form .print-note-card {
            border: 1px solid #000000 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            background: #ffffff !important;
            break-inside: avoid;
            page-break-inside: avoid;
        }

        .delegation-referral-form .p-inputtext,
        .delegation-referral-form .p-inputtextarea,
        .delegation-referral-form input[type="date"] {
            border: 1px solid #000000 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            background: #ffffff !important;
            color: #000000 !important;
        }

        .delegation-referral-form .binder-static-value {
            width: 100% !important;
            min-height: 44px !important;
            padding: 0.75rem 0.85rem !important;
            border: 1px solid #000000 !important;
            border-radius: 0 !important;
            background: #ffffff !important;
            color: #000000 !important;
            white-space: pre-wrap !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            line-height: 1.5 !important;
        }

        .delegation-referral-form .binder-static-choice {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 1rem !important;
            min-width: 1rem !important;
            color: #000000 !important;
            font-weight: 700 !important;
        }

        .delegation-referral-form .p-float-label.print-static-field {
            display: flex !important;
            flex-direction: column-reverse !important;
            gap: 0.45rem !important;
        }

        .delegation-referral-form .p-float-label.print-static-field label {
            margin-bottom: 0 !important;
        }

        .delegation-referral-form .referral-layout-shell,
        .delegation-referral-form .referral-layout-shell .grid,
        .delegation-referral-form .referral-layout-shell .col-12,
        .delegation-referral-form .referral-form-card {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
        }
    }
`;

const NurseDelegationReferralFormPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { residentId, homeId, formCode: routeFormCode } = useParams();
    const { authStore, backendAPIStore } = useContext(MobXProviderContext);
    const toast = useRef<Toast>(null);
    const queryParams = new URLSearchParams(location.search);
    const requestedFormId = String(queryParams.get("formId") || "").trim();
    const activeFormCode = String(routeFormCode || "01-212").trim().toUpperCase();
    const isStructuredReferralForm = activeFormCode === "01-212";
    const isCompactDelegationForm = !isStructuredReferralForm;
    const standardFieldShellStyle = isCompactDelegationForm
        ? {
            border: "none",
            borderRadius: 0,
            background: "transparent",
            padding: "0.15rem 0",
            height: "auto",
        }
        : fieldShellStyle;
    const standardOptionChipStyle = isCompactDelegationForm
        ? {
            ...optionChipStyle,
            minHeight: "2.25rem",
            padding: "0.35rem 0.45rem",
            border: "none",
            borderRadius: 0,
            boxShadow: "none",
            background: "transparent",
            gap: "0.45rem",
        }
        : optionChipStyle;
    const standardSectionSubtitleStyle = isCompactDelegationForm
        ? { ...sectionSubtitleStyle, marginBottom: "0.55rem", fontSize: "0.84rem", lineHeight: 1.35 }
        : sectionSubtitleStyle;
    const standardFormBodyStyle = isCompactDelegationForm
        ? {
            ...layoutShellStyle,
            background: "linear-gradient(rgb(255 255 255) 0%, rgb(255, 255, 255) 100%)",
            border: "1px solid rgb(255, 222, 184)",
            borderRadius: 18,
            boxShadow: "0 10px 24px rgba(15, 61, 99, 0.06)",
            padding: "0.95rem 1rem 0.75rem",
        }
        : layoutShellStyle;
    const compactSectionHeadingStyle = {
        color: "#9a3412",
        fontSize: "0.78rem",
        fontWeight: 800,
        letterSpacing: "0.04em",
        textTransform: "uppercase" as const,
        padding: "0.55rem 0 0.1rem",
        borderBottom: "1px solid #ffedd5",
    };
    const formConfig = getDelegationFormConfig(activeFormCode) || getDelegationFormConfig("01-212");

    const userRoleType = authStore.getUserRoleType();
    const userGroups = authStore.getUserGroups() || [];
    const isAdminUser = userGroups.includes(USER_GROUP_ADMIN) || userRoleType === ROLE_TYPE_ADMIN;
    const isProviderNurseResidentMode = location.pathname.startsWith("/provider/nurse/");
    const isSharedResidentMode = location.pathname.startsWith("/shared/home/") && location.pathname.includes("/delegation/form/");
    const isProviderResidentMode = location.pathname.startsWith("/home/") && location.pathname.includes("/delegation/form/");
    const usesProviderDelegationApi = isProviderResidentMode || isProviderNurseResidentMode || isSharedResidentMode;

    const [residentProfile, setResidentProfile] = useState<any | null>(null);
    const [formData, setFormData] = useState<any>(createDefaultForm());
    const [loadingForm, setLoadingForm] = useState(true);
    const [savingDraft, setSavingDraft] = useState(false);
    const [formId, setFormId] = useState("");
    const [formVersion, setFormVersion] = useState<number | null>(null);
    const [formStatus, setFormStatus] = useState<DelegationFormStatus>(DELEGATION_FORM_STATUS.DRAFT);
    const [, setFormUpdatedAt] = useState("");
    const [formThreadId, setFormThreadId] = useState("");
    const [formThreadLabel, setFormThreadLabel] = useState("");
    const [formStaffName, setFormStaffName] = useState("");
    const [templateVersion, setTemplateVersion] = useState("");
    const [templateSchemaVersion, setTemplateSchemaVersion] = useState<number | null>(null);
    const [templateDefinition, setTemplateDefinition] = useState<StandardDelegationFormDefinition | null>(null);
    const [showRenameDialog, setShowRenameDialog] = useState(false);
    const [pendingThreadLabel, setPendingThreadLabel] = useState("");
    const [showFeatureAccessDialog, setShowFeatureAccessDialog] = useState(false);
    const [featureAccessDialogMessage, setFeatureAccessDialogMessage] = useState("");
    const [featureAccessUnlockEligible, setFeatureAccessUnlockEligible] = useState(false);
    const [featureAccessUnlockExpiresAt, setFeatureAccessUnlockExpiresAt] = useState<string | null>(null);
    const [loadingFeatureAccessUnlockState, setLoadingFeatureAccessUnlockState] = useState(false);
    const [activatingFeatureAccessUnlock, setActivatingFeatureAccessUnlock] = useState(false);
    const [featureAccessUnlockError, setFeatureAccessUnlockError] = useState("");
    const [savingToBinder, setSavingToBinder] = useState(false);
    const [savingThreadLabel, setSavingThreadLabel] = useState(false);
    const [providerCanUseNurseWorkspace, setProviderCanUseNurseWorkspace] = useState(false);
    const [showInstructions, setShowInstructions] = useState(false);
    const requiresProviderSubscriptionMessageOnly = isSharedResidentMode;
    const canShowProviderNurseDocumentActions = userRoleType === ROLE_TYPE_PROVIDER
        && providerCanUseNurseWorkspace
        && (isProviderResidentMode || isProviderNurseResidentMode);
    const canSaveDelegationToBinder = Boolean(residentId);

    useEffect(() => {
        let isMounted = true;

        const loadProviderWorkspaceCapability = async () => {
            if (isMounted) {
                setProviderCanUseNurseWorkspace(userRoleType === ROLE_TYPE_PROVIDER);
            }
        };

        void loadProviderWorkspaceCapability();

        return () => {
            isMounted = false;
        };
    }, [authStore, backendAPIStore, userRoleType]);
    const caseRequestRef = useRef<HTMLDivElement | null>(null);
    const clientInfoRef = useRef<HTMLDivElement | null>(null);
    const receiptRef = useRef<HTMLDivElement | null>(null);
    const responseRef = useRef<HTMLDivElement | null>(null);
    const printShellRef = useRef<HTMLDivElement | null>(null);
    const featureAccessRetryActionRef = useRef<null | (() => Promise<void>)>(null);

    const buildDefaultFormData = (
        residentName = "",
        explicitTemplateVersion?: string | null,
        explicitTemplateDefinition?: StandardDelegationFormDefinition | null,
    ) => (
        normalizeDelegationFormData(
            activeFormCode,
            isStructuredReferralForm
                ? createDefaultForm(residentName)
                : createDefaultStandardDelegationForm(
                    residentName,
                    formConfig?.title || "",
                    activeFormCode,
                    explicitTemplateVersion || templateVersion || getLatestDelegationTemplateVersion(activeFormCode),
                    explicitTemplateDefinition || templateDefinition,
                ),
            residentName,
        )
    );
    const standardFormDefinition = !isStructuredReferralForm
        ? getDelegationTemplateDefinition(activeFormCode, templateVersion, templateDefinition)
        : null;
    const formInstructionsTitle = isStructuredReferralForm
        ? ""
        : getDelegationInstructionsTitle(activeFormCode, templateVersion, templateDefinition);
    const formInstructions = isStructuredReferralForm
        ? []
        : getDelegationInstructions(activeFormCode, templateVersion, templateDefinition);
    const formNotes = isStructuredReferralForm
        ? []
        : (standardFormDefinition?.notes || getDelegationFormNotes(activeFormCode));
    const printHint = isStructuredReferralForm
        ? ""
        : (standardFormDefinition?.printHint || getDelegationPrintHint(activeFormCode));
    const printHeader = isStructuredReferralForm
        ? null
        : getDelegationPrintHeader(activeFormCode, templateVersion, templateDefinition);
    const printFooter = isStructuredReferralForm
        ? []
        : getDelegationPrintFooter(activeFormCode, templateVersion, templateDefinition);
    const currentDisplayTitle = buildDelegationDisplayTitle(formConfig?.title || "Delegation Form", formThreadLabel, formStaffName);
    const renameTitlePrefix = `${formConfig?.id || activeFormCode} ${String(formConfig?.title || "Delegation Form").trim()} - `;
    const renamePreviewTitle = `${renameTitlePrefix}${String(pendingThreadLabel || "").trim()}`.trim();
    const isNurseUser = userRoleType === ROLE_TYPE_NURSE;
    const formattedFeatureAccessUnlockExpiryDate = featureAccessUnlockExpiresAt
        ? new Intl.DateTimeFormat("en-US", {
            timeZone: "America/Los_Angeles",
            month: "short",
            day: "numeric",
            year: "numeric",
        }).format(new Date(featureAccessUnlockExpiresAt))
        : "";

    useEffect(() => {
        let ignore = false;

        const loadFeatureAccessUnlockState = async () => {
            if (!showFeatureAccessDialog || !isNurseUser) {
                if (!ignore) {
                    setFeatureAccessUnlockEligible(false);
                    setFeatureAccessUnlockExpiresAt(null);
                    setFeatureAccessUnlockError("");
                }
                return;
            }

            const userId = authStore.getUserId();
            if (!userId) {
                return;
            }

            try {
                setLoadingFeatureAccessUnlockState(true);
                setFeatureAccessUnlockError("");
                await backendAPIStore.init();
                const subscriptionDetails = await backendAPIStore.getSubscription(userId, ROLE_TYPE_NURSE);
                if (ignore) {
                    return;
                }
                setFeatureAccessUnlockEligible(!!subscriptionDetails?.accessUnlockEligible);
                setFeatureAccessUnlockExpiresAt(String(subscriptionDetails?.accessUnlockExpiresAt || "").trim() || null);
            } catch (error: any) {
                if (!ignore) {
                    setFeatureAccessUnlockEligible(false);
                    setFeatureAccessUnlockExpiresAt(null);
                    setFeatureAccessUnlockError(String(error?.message || "").trim());
                }
            } finally {
                if (!ignore) {
                    setLoadingFeatureAccessUnlockState(false);
                }
            }
        };

        void loadFeatureAccessUnlockState();

        return () => {
            ignore = true;
        };
    }, [authStore, backendAPIStore, isNurseUser, showFeatureAccessDialog]);

    useEffect(() => {
        const loadResidentProfile = async () => {
            if (!residentId || !formConfig) return;
            setLoadingForm(true);
            try {
                await backendAPIStore.init();
                const response = (isProviderResidentMode || isSharedResidentMode)
                    ? await backendAPIStore.getProviderResidentProfile(residentId)
                    : await backendAPIStore.getNurseResidentProfile(residentId);
                setResidentProfile(response || null);

                const residentName = `${response?.firstName || ""} ${response?.lastName || ""}`.trim();
                const versionResponse: any = usesProviderDelegationApi
                    ? await backendAPIStore.getProviderDelegationForms(residentId, activeFormCode)
                    : await backendAPIStore.getNurseDelegationForms(residentId, activeFormCode);
                const draftResponse: any = requestedFormId
                    ? (usesProviderDelegationApi
                        ? await backendAPIStore.getProviderDelegationFormRecords(requestedFormId)
                        : await backendAPIStore.getNurseDelegationFormRecords(requestedFormId))
                    : versionResponse;

                if (requestedFormId) {
                    const records = Array.isArray(draftResponse) ? draftResponse : [];
                    const header = records.find((item: any) => item.recordType === "HEADER") || null;
                    const fullFormRecord = records.find(
                        (item: any) => item.recordType === "SECTION#FULL_FORM" || item.sectionKey === "FULL_FORM",
                    );
                    if (header?.formId) {
                        const savedTemplateVersion = String(header?.templateVersion || "").trim() || getLatestDelegationTemplateVersion(activeFormCode);
                        const savedTemplateDefinition = (header?.templateDefinition || null) as StandardDelegationFormDefinition | null;
                        const resolvedTemplateState = resolveStandardFormTemplateState(
                            activeFormCode,
                            savedTemplateVersion,
                            savedTemplateDefinition,
                        );
                        setFormId(header.formId);
                        setFormVersion(header?.version || null);
                        setFormStatus(normalizeDelegationStatus(header?.status));
                        setFormUpdatedAt(header?.updatedAt || header?.createdAt || "");
                        setFormThreadId(getDelegationThreadId(header));
                        setFormThreadLabel(getDelegationThreadLabel(header));
                        setFormStaffName(String(header?.selectedStaff?.[0]?.name || "").trim());
                        setTemplateVersion(resolvedTemplateState.templateVersion);
                        setTemplateSchemaVersion(resolvedTemplateState.templateSchemaVersion);
                        setTemplateDefinition(resolvedTemplateState.templateDefinition);
                        setFormData(normalizeDelegationFormData(
                            activeFormCode,
                            {
                                ...buildDefaultFormData(
                                    residentName,
                                    resolvedTemplateState.templateVersion,
                                    resolvedTemplateState.templateDefinition,
                                ),
                                ...(fullFormRecord?.sectionData || {}),
                            },
                            residentName,
                        ));
                        return;
                    }
                }

                const forms = draftResponse?.forms || [];
                const activeLoadedFormId = draftResponse?.activeFormId || forms[0]?.formId || "";
                const activeHeader = forms.find((item: any) => item.formId === activeLoadedFormId) || forms[0] || null;

                if (activeLoadedFormId) {
                    const savedTemplateVersion = String(activeHeader?.templateVersion || "").trim() || getLatestDelegationTemplateVersion(activeFormCode);
                    const savedTemplateDefinition = (activeHeader?.templateDefinition || null) as StandardDelegationFormDefinition | null;
                    const resolvedTemplateState = resolveStandardFormTemplateState(
                        activeFormCode,
                        savedTemplateVersion,
                        savedTemplateDefinition,
                    );
                    setFormId(activeLoadedFormId);
                    setFormVersion(activeHeader?.version || null);
                    setFormStatus(normalizeDelegationStatus(activeHeader?.status));
                    setFormUpdatedAt(activeHeader?.updatedAt || activeHeader?.createdAt || "");
                    setFormThreadId(getDelegationThreadId(activeHeader));
                    setFormThreadLabel(getDelegationThreadLabel(activeHeader));
                    setFormStaffName(String(activeHeader?.selectedStaff?.[0]?.name || "").trim());
                    setTemplateVersion(resolvedTemplateState.templateVersion);
                    setTemplateSchemaVersion(resolvedTemplateState.templateSchemaVersion);
                    setTemplateDefinition(resolvedTemplateState.templateDefinition);
                    const fullFormRecord = (draftResponse?.activeRecords || []).find(
                        (item: any) => item.recordType === "SECTION#FULL_FORM" || item.sectionKey === "FULL_FORM",
                    );
                    setFormData(normalizeDelegationFormData(
                        activeFormCode,
                        {
                            ...buildDefaultFormData(
                                residentName,
                                resolvedTemplateState.templateVersion,
                                resolvedTemplateState.templateDefinition,
                            ),
                            ...(fullFormRecord?.sectionData || {}),
                        },
                        residentName,
                    ));
                } else {
                    const nextTemplateDefinition = getDelegationTemplateDefinition(activeFormCode);
                    const created: any = usesProviderDelegationApi
                        ? await backendAPIStore.createProviderDelegationForm(residentId, activeFormCode, formConfig.title, {
                            templateVersion: nextTemplateDefinition?.templateVersion,
                            templateSchemaVersion: nextTemplateDefinition?.schemaVersion,
                            templateRevisionLabel: nextTemplateDefinition?.printHeader?.revision,
                            templateDefinition: nextTemplateDefinition,
                        })
                        : await backendAPIStore.createNurseDelegationForm(residentId, activeFormCode, formConfig.title, {
                            templateVersion: nextTemplateDefinition?.templateVersion,
                            templateSchemaVersion: nextTemplateDefinition?.schemaVersion,
                            templateRevisionLabel: nextTemplateDefinition?.printHeader?.revision,
                            templateDefinition: nextTemplateDefinition,
                        });
                    setFormId(created?.formId || "");
                    setFormVersion(created?.version || null);
                    setFormStatus(DELEGATION_FORM_STATUS.DRAFT);
                    setFormUpdatedAt("");
                    setFormThreadId(String(created?.formThreadId || "").trim() || "default");
                    setFormThreadLabel(String(created?.formThreadLabel || "").trim());
                    setFormStaffName(String(created?.selectedStaff?.[0]?.name || "").trim());
                    setTemplateVersion(String(created?.templateVersion || nextTemplateDefinition?.templateVersion || "").trim());
                    setTemplateSchemaVersion(Number(created?.templateSchemaVersion || nextTemplateDefinition?.schemaVersion || 1));
                    setTemplateDefinition((created?.templateDefinition || nextTemplateDefinition || null) as StandardDelegationFormDefinition | null);
                    setFormData(buildDefaultFormData(
                        residentName,
                        String(created?.templateVersion || nextTemplateDefinition?.templateVersion || "").trim(),
                        (created?.templateDefinition || nextTemplateDefinition || null) as StandardDelegationFormDefinition | null,
                    ));
                }
            } catch (error: any) {
                toast.current?.show({
                    severity: "error",
                    summary: getErrorTitle(error),
                    detail: error?.message || "Unable to load the delegation form.",
                    life: 4000,
                });
            } finally {
                setLoadingForm(false);
            }
        };

        void loadResidentProfile();
    }, [residentId, backendAPIStore, requestedFormId, activeFormCode, formConfig?.title, isProviderResidentMode, usesProviderDelegationApi]);

    const updateField = (field: string, value: any) => {
        setFormData((prev: any) => ({ ...prev, [field]: value }));
    };

    const updatePrnOrderField = (index: number, field: string, value: string) => {
        setFormData((prev: any) => {
            const currentOrders = Array.isArray(prev?.prnOrders) ? prev.prnOrders : [createEmptyPrnMedicationOrder()];
            const nextOrders = currentOrders.map((order: any, orderIndex: number) =>
                orderIndex === index
                    ? { ...order, [field]: value }
                    : order
            );
            return { ...prev, prnOrders: nextOrders };
        });
    };

    const addPrnOrder = () => {
        setFormData((prev: any) => {
            const currentOrders = Array.isArray(prev?.prnOrders) ? prev.prnOrders : [];
            return {
                ...prev,
                prnOrders: [...currentOrders, createEmptyPrnMedicationOrder()],
            };
        });
    };

    const removePrnOrder = (index: number) => {
        setFormData((prev: any) => {
            const currentOrders = Array.isArray(prev?.prnOrders) ? prev.prnOrders : [];
            const remainingOrders = currentOrders.filter((_: any, orderIndex: number) => orderIndex !== index);
            return {
                ...prev,
                prnOrders: remainingOrders.length > 0 ? remainingOrders : [createEmptyPrnMedicationOrder()],
            };
        });
    };

    const syncRescindingDelegationRows = (rows: Array<{ caregiverName: string; medicationOrTreatment: string; notes: string }>) => {
        const normalizedRows = rows.length > 0 ? rows : [createEmptyRescindingDelegationRow()];
        setFormData((prev: any) => ({
            ...prev,
            rescindedEntries: normalizedRows,
            caregiverNames: normalizedRows.map((row) => String(row?.caregiverName || "").trim()).filter(Boolean).join("\n"),
            rescindedTasks: normalizedRows.map((row) => String(row?.medicationOrTreatment || "").trim()).filter(Boolean).join("\n"),
            notes: normalizedRows.map((row) => String(row?.notes || "").trim()).filter(Boolean).join("\n"),
        }));
    };

    const addRescindingDelegationRow = () => {
        const currentRows = Array.isArray(formData?.rescindedEntries) ? formData.rescindedEntries : [];
        if (currentRows.length >= RESCINDING_DELEGATION_MAX_ROWS) {
            return;
        }
        syncRescindingDelegationRows([...currentRows, createEmptyRescindingDelegationRow()]);
    };

    const updateRescindingDelegationRowField = (
        index: number,
        field: "caregiverName" | "medicationOrTreatment" | "notes",
        value: string,
    ) => {
        const currentRows = Array.isArray(formData?.rescindedEntries) ? formData.rescindedEntries : [createEmptyRescindingDelegationRow()];
        const nextRows = currentRows.map((row: any, rowIndex: number) => (
            rowIndex === index ? { ...row, [field]: value } : row
        ));
        syncRescindingDelegationRows(nextRows);
    };

    const removeRescindingDelegationRow = (index: number) => {
        const currentRows = Array.isArray(formData?.rescindedEntries) ? formData.rescindedEntries : [];
        const remainingRows = currentRows.filter((_: any, rowIndex: number) => rowIndex !== index);
        syncRescindingDelegationRows(remainingRows);
    };

    const syncLtcwCompetencyEntries = (rows: Array<ReturnType<typeof createEmptyLtcwCompetencyEntry>>) => {
        const normalizedRows = rows.length > 0 ? rows : [createEmptyLtcwCompetencyEntry()];
        setFormData((prev: any) => ({
            ...prev,
            ltcwCompetencyEntries: normalizedRows,
        }));
    };

    const addLtcwCompetencyEntry = () => {
        const currentRows = Array.isArray(formData?.ltcwCompetencyEntries) ? formData.ltcwCompetencyEntries : [];
        syncLtcwCompetencyEntries([...currentRows, createEmptyLtcwCompetencyEntry()]);
    };

    const updateLtcwCompetencyEntryField = (
        index: number,
        field: keyof ReturnType<typeof createEmptyLtcwCompetencyEntry>,
        value: string | boolean,
    ) => {
        const currentRows = Array.isArray(formData?.ltcwCompetencyEntries) ? formData.ltcwCompetencyEntries : [createEmptyLtcwCompetencyEntry()];
        const nextRows = currentRows.map((row: any, rowIndex: number) => (
            rowIndex === index ? { ...row, [field]: value } : row
        ));
        syncLtcwCompetencyEntries(nextRows);
    };

    const updateLtcwCompetencyMethods = (index: number, selectedMethods: string[]) => {
        const selected = new Set(selectedMethods);
        setFormData((prev: any) => {
            const currentRows = Array.isArray(prev?.ltcwCompetencyEntries)
                ? prev.ltcwCompetencyEntries
                : [createEmptyLtcwCompetencyEntry()];
            const nextRows = currentRows.map((row: any, rowIndex: number) => (
                rowIndex === index
                    ? {
                        ...row,
                        observationDemonstration: selected.has("Observation or Demonstration"),
                        verbalDescription: selected.has("Verbal Description"),
                        recordReview: selected.has("Record Review"),
                    }
                    : row
            ));
            return { ...prev, ltcwCompetencyEntries: nextRows };
        });
    };

    const removeLtcwCompetencyEntry = (index: number) => {
        const currentRows = Array.isArray(formData?.ltcwCompetencyEntries) ? formData.ltcwCompetencyEntries : [];
        const remainingRows = currentRows.filter((_: any, rowIndex: number) => rowIndex !== index);
        syncLtcwCompetencyEntries(remainingRows);
    };

    const toggleArrayValue = (field: "offices" | "methods" | "attachments", value: string) => {
        setFormData((prev: any) => {
            const current = Array.isArray(prev[field]) ? prev[field] : [];
            const next = current.includes(value)
                ? current.filter((item: string) => item !== value)
                : [...current, value];
            return { ...prev, [field]: next };
        });
    };

    const showDelegationSubscriptionDialog = (error: any, retryAction?: (() => Promise<void>) | null) => {
        setFeatureAccessDialogMessage(
            requiresProviderSubscriptionMessageOnly
                ? "The provider must have an active subscription to use the shared resident delegation workspace."
                : (error?.message
                    || "Saving nurse delegation forms requires an active nurse subscription. Subscribe to continue saving drafts and completed forms.")
        );
        featureAccessRetryActionRef.current = retryAction || null;
        setFeatureAccessUnlockError("");
        setShowFeatureAccessDialog(true);
    };

    const handleActivateFeatureAccessUnlock = async () => {
        try {
            setActivatingFeatureAccessUnlock(true);
            setFeatureAccessUnlockError("");
            await backendAPIStore.init();
            await backendAPIStore.activateAccessUnlock();
            setFeatureAccessUnlockEligible(false);
            setShowFeatureAccessDialog(false);
            const retryAction = featureAccessRetryActionRef.current;
            featureAccessRetryActionRef.current = null;
            if (retryAction) {
                await retryAction();
                return;
            }
            window.location.reload();
        } catch (error: any) {
            setFeatureAccessUnlockError(String(error?.message || "Unable to unlock access right now.").trim());
        } finally {
            setActivatingFeatureAccessUnlock(false);
        }
    };

    const openRenameDialog = () => {
        setPendingThreadLabel(formThreadLabel);
        setShowRenameDialog(true);
    };

    const saveThreadLabel = async () => {
        const normalizedNextLabel = String(pendingThreadLabel || "").trim();
        if (normalizedNextLabel === String(formThreadLabel || "").trim()) {
            setShowRenameDialog(false);
            return;
        }

        if (!formId) {
            setFormThreadLabel(normalizedNextLabel);
            setShowRenameDialog(false);
            toast.current?.show({
                severity: "success",
                summary: "File name updated",
                detail: "The file name will be used when you save this delegation form.",
                life: 3000,
            });
            return;
        }

        setSavingThreadLabel(true);
        try {
            await backendAPIStore.init();
            const response: any = usesProviderDelegationApi
                ? await backendAPIStore.updateProviderDelegationForm({
                    formId,
                    formThreadLabel: normalizedNextLabel,
                })
                : await backendAPIStore.updateNurseDelegationForm({
                    formId,
                    formThreadLabel: normalizedNextLabel,
                });
            setFormThreadLabel(String(response?.formThreadLabel || normalizedNextLabel).trim());
            setFormUpdatedAt(new Date().toISOString());
            setShowRenameDialog(false);
            toast.current?.show({
                severity: "success",
                summary: "File name updated",
                detail: "The delegation form file name was updated.",
                life: 3000,
            });
        } catch (error: any) {
            if (error?.code === "SUBSCRIPTION_LIMIT_REACHED" || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED") {
                showDelegationSubscriptionDialog(error, saveThreadLabel);
                return;
            }
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Failed to update the delegation form file name.",
                life: 4000,
            });
        } finally {
            setSavingThreadLabel(false);
        }
    };

    const saveDraft = async () => {
        if (!residentId || !formConfig) return;

        setSavingDraft(true);
        try {
            await backendAPIStore.init();

            let activeFormId = formId;
            let activeVersion = formVersion;
            if (!activeFormId) {
                const nextTemplateDefinition = standardFormDefinition || getDelegationTemplateDefinition(activeFormCode);
                const created: any = usesProviderDelegationApi
                    ? await backendAPIStore.createProviderDelegationForm(residentId, activeFormCode, formConfig.title, {
                        formThreadId,
                        formThreadLabel,
                        templateVersion: nextTemplateDefinition?.templateVersion,
                        templateSchemaVersion: nextTemplateDefinition?.schemaVersion,
                        templateRevisionLabel: nextTemplateDefinition?.printHeader?.revision,
                        templateDefinition: nextTemplateDefinition,
                    })
                    : await backendAPIStore.createNurseDelegationForm(residentId, activeFormCode, formConfig.title, {
                        formThreadId,
                        formThreadLabel,
                        templateVersion: nextTemplateDefinition?.templateVersion,
                        templateSchemaVersion: nextTemplateDefinition?.schemaVersion,
                        templateRevisionLabel: nextTemplateDefinition?.printHeader?.revision,
                        templateDefinition: nextTemplateDefinition,
                    });
                activeFormId = created?.formId || "";
                activeVersion = created?.version || null;
                setFormId(activeFormId);
                setFormVersion(activeVersion);
                setFormStatus(DELEGATION_FORM_STATUS.DRAFT);
                setFormThreadId(String(created?.formThreadId || "").trim() || "default");
                setFormThreadLabel(String(created?.formThreadLabel || "").trim());
                setTemplateVersion(String(created?.templateVersion || nextTemplateDefinition?.templateVersion || "").trim());
                setTemplateSchemaVersion(Number(created?.templateSchemaVersion || nextTemplateDefinition?.schemaVersion || 1));
                setTemplateDefinition((created?.templateDefinition || nextTemplateDefinition || null) as StandardDelegationFormDefinition | null);
            }

            if (usesProviderDelegationApi) {
                await backendAPIStore.updateProviderDelegationForm({
                    formId: activeFormId,
                    sectionKey: "FULL_FORM",
                    sectionData: formData,
                    status: DELEGATION_FORM_STATUS.DRAFT,
                });
            } else {
                await backendAPIStore.updateNurseDelegationForm({
                    formId: activeFormId,
                    sectionKey: "FULL_FORM",
                    sectionData: formData,
                    status: DELEGATION_FORM_STATUS.DRAFT,
                });
            }

            toast.current?.show({
                severity: "success",
                summary: "Draft saved",
                detail: `Version ${activeVersion || 1} was saved to the resident record.`,
                life: 3000,
            });
            setFormUpdatedAt(new Date().toISOString());
        } catch (error: any) {
            if (error?.code === "SUBSCRIPTION_LIMIT_REACHED" || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED") {
                showDelegationSubscriptionDialog(error, saveDraft);
                return;
            }
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Failed to save the referral draft.",
                life: 4000,
            });
        } finally {
            setSavingDraft(false);
        }
    };

    const markAsComplete = async () => {
        if (!residentId || !formConfig) return;

        setSavingDraft(true);
        try {
            await backendAPIStore.init();

            let activeFormId = formId;
            let activeVersion = formVersion;
            if (!activeFormId) {
                const nextTemplateDefinition = standardFormDefinition || getDelegationTemplateDefinition(activeFormCode);
                const created: any = usesProviderDelegationApi
                    ? await backendAPIStore.createProviderDelegationForm(residentId, activeFormCode, formConfig.title, {
                        formThreadId,
                        formThreadLabel,
                        templateVersion: nextTemplateDefinition?.templateVersion,
                        templateSchemaVersion: nextTemplateDefinition?.schemaVersion,
                        templateRevisionLabel: nextTemplateDefinition?.printHeader?.revision,
                        templateDefinition: nextTemplateDefinition,
                    })
                    : await backendAPIStore.createNurseDelegationForm(residentId, activeFormCode, formConfig.title, {
                        formThreadId,
                        formThreadLabel,
                        templateVersion: nextTemplateDefinition?.templateVersion,
                        templateSchemaVersion: nextTemplateDefinition?.schemaVersion,
                        templateRevisionLabel: nextTemplateDefinition?.printHeader?.revision,
                        templateDefinition: nextTemplateDefinition,
                    });
                activeFormId = created?.formId || "";
                activeVersion = created?.version || null;
                setFormId(activeFormId);
                setFormVersion(activeVersion);
                setFormThreadId(String(created?.formThreadId || "").trim() || "default");
                setFormThreadLabel(String(created?.formThreadLabel || "").trim());
                setTemplateVersion(String(created?.templateVersion || nextTemplateDefinition?.templateVersion || "").trim());
                setTemplateSchemaVersion(Number(created?.templateSchemaVersion || nextTemplateDefinition?.schemaVersion || 1));
                setTemplateDefinition((created?.templateDefinition || nextTemplateDefinition || null) as StandardDelegationFormDefinition | null);
            }

            if (usesProviderDelegationApi) {
                await backendAPIStore.updateProviderDelegationForm({
                    formId: activeFormId,
                    sectionKey: "FULL_FORM",
                    sectionData: formData,
                    status: DELEGATION_FORM_STATUS.COMPLETED,
                });
            } else {
                await backendAPIStore.updateNurseDelegationForm({
                    formId: activeFormId,
                    sectionKey: "FULL_FORM",
                    sectionData: formData,
                    status: DELEGATION_FORM_STATUS.COMPLETED,
                });
            }

            setFormStatus(DELEGATION_FORM_STATUS.COMPLETED);
            setFormUpdatedAt(new Date().toISOString());
            toast.current?.show({
                severity: "success",
                summary: "Form marked complete",
                detail: `Version ${activeVersion || 1} was marked as complete for this resident.`,
                life: 3000,
            });
        } catch (error: any) {
            if (error?.code === "SUBSCRIPTION_LIMIT_REACHED" || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED") {
                showDelegationSubscriptionDialog(error, markAsComplete);
                return;
            }
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Failed to mark this delegation form as complete.",
                life: 4000,
            });
        } finally {
            setSavingDraft(false);
        }
    };

    const resetForm = () => {
        const residentName = `${residentProfile?.firstName || ""} ${residentProfile?.lastName || ""}`.trim();
        const next = buildDefaultFormData(residentName);
        setFormData(next);
        toast.current?.show({
            severity: "info",
            summary: "Form reset",
            detail: "The referral form fields were cleared locally.",
            life: 2500,
        });
    };

    const renderCheckGroup = (label: string, field: "offices" | "methods" | "attachments", options: string[]) => (
        <div style={isCompactDelegationForm ? standardFieldShellStyle : fieldShellStyle}>
            <div style={fieldLabelStyle}>{label}</div>
            <div style={optionGroupStyle}>
                {options.map((option) => (
                    <label key={option} style={isCompactDelegationForm ? standardOptionChipStyle : optionChipStyle}>
                        <span style={{ lineHeight: 1.4 }}>{option}</span>
                        <Checkbox
                            inputId={`${field}-${option}`}
                            checked={(formData[field] || []).includes(option)}
                            onChange={() => toggleArrayValue(field, option)}
                        />
                    </label>
                ))}
            </div>
        </div>
    );

    const renderBooleanToggle = (label: string, checked: boolean, onChange: (checked: boolean) => void) => (
        <label
            style={{
                ...optionChipStyle,
                width: "auto",
                minWidth: "fit-content",
                maxWidth: "100%",
            }}
        >
            <span>{label}</span>
            <Checkbox
                inputId={label.replace(/\s+/g, "-").toLowerCase()}
                checked={checked}
                onChange={(e) => onChange(Boolean(e.checked))}
            />
        </label>
    );

    const renderRadioGroup = (
        label: string,
        name: string,
        value: string,
        options: Array<{ label: string; value: string }>,
        onChange: (value: string) => void,
    ) => (
        <div style={isCompactDelegationForm ? standardFieldShellStyle : fieldShellStyle}>
            <div style={fieldLabelStyle}>{label}</div>
            <div style={optionGroupStyle}>
                {options.map((option) => (
                    <label key={option.value} style={isCompactDelegationForm ? standardOptionChipStyle : optionChipStyle}>
                        <span style={{ lineHeight: 1.4 }}>{option.label}</span>
                        <RadioButton
                            inputId={`${name}-${option.value}`}
                            name={name}
                            value={option.value}
                            checked={value === option.value}
                            onChange={() => onChange(option.value)}
                        />
                    </label>
                ))}
            </div>
        </div>
    );

    const renderSectionHeader = (title: string, subtitle: string) => (
        <>
            <div style={sectionTitleStyle}>{title}</div>
            <div style={standardSectionSubtitleStyle}>{subtitle}</div>
        </>
    );

    const parseDateValue = (value?: string) => {
        const normalized = String(value || "").trim();
        if (!normalized) return null;
        const parsed = new Date(`${normalized}T12:00:00`);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const formatDateValue = (value: Date | null | undefined) => {
        if (!value) return "";
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const renderDateField = (
        id: string,
        label: string,
        value: string,
        onChange: (nextValue: string) => void,
        calendarStyle?: React.CSSProperties,
        flat = false,
    ) => {
        const dateControl = (
            <Calendar
                inputId={id}
                value={parseDateValue(value)}
                onChange={(e) => onChange(formatDateValue((e.value as Date | null) || null))}
                dateFormat="mm/dd/yy"
                style={{ ...calendarFieldStyle, ...calendarStyle }}
                inputStyle={calendarInputStyle}
            />
        );
        return flat ? (
            <>
                <label htmlFor={id} style={fieldLabelStyle}>{label}</label>
                {dateControl}
            </>
        ) : (
            <div style={standardFieldShellStyle}>
                <label htmlFor={id} style={fieldLabelStyle}>{label}</label>
                {dateControl}
            </div>
        );
    };

    const resolveStandardFieldCols = (fieldConfig: StandardDelegationFieldConfig) => {
        const requestedCols = String(fieldConfig.cols || "col-12").trim();
        const label = String(fieldConfig.label || "").trim().toLowerCase();
        const fieldName = String(fieldConfig.field || "").trim().toLowerCase();
        const isSignatureField = label.includes("signature") || fieldName.includes("signature");
        const isDateField = fieldConfig.kind === "date" || label.includes(" date") || label.startsWith("date ");
        const isSingleCheckboxField = fieldConfig.kind === "checkbox";
        const isLongTextField = fieldConfig.kind === "textarea" && (
            label.length > 34
            || label.includes("list ")
            || label.includes("dosages")
            || label.includes("frequency")
            || label.includes("instructions")
            || label.includes("expected outcome")
            || label.includes("comments")
            || label.includes("reason")
            || label.includes("notes")
            || label.includes("report")
            || label.includes("plan")
            || label.includes("details")
            || fieldName.includes("instructions")
            || fieldName.includes("comments")
            || fieldName.includes("reason")
            || fieldName.includes("notes")
            || fieldName.includes("report")
            || fieldName.includes("plan")
            || fieldName.includes("details")
        );

        if (isCompactDelegationForm) {
            if (activeFormCode === "13-678") {
                return fieldName === "delegatedtaskandexpectedoutcome" || fieldName === "stepstoperformtask"
                    ? "col-12"
                    : "col-12 md:col-4";
            }
            if (fieldName === "additionalteachingaidesattached" || fieldName === "additionalteachingaides") {
                return "col-12 md:col-6";
            }
            return isLongTextField ? "col-12" : "col-12 md:col-4";
        }

        if (isSignatureField) {
            return "col-12 md:col-8";
        }

        if (isDateField) {
            return "col-12 md:col-4";
        }

        if (isSingleCheckboxField && requestedCols === "col-12") {
            return "col-12 md:col-4";
        }

        if (isLongTextField) {
            return "col-12";
        }

        return requestedCols;
    };

    const renderTextField = (id: string, label: string, value: string, field: string) => (
        <div style={standardFieldShellStyle}>
            <label htmlFor={id} style={fieldLabelStyle}>{label}</label>
            <InputText
                id={id}
                value={value}
                onChange={(e) => updateField(field, e.target.value)}
                style={{ width: "100%" }}
            />
        </div>
    );

    const renderTextareaField = (
        id: string,
        label: string,
        value: string,
        rows: number,
        onChange: (nextValue: string) => void,
    ) => (
        <div style={standardFieldShellStyle}>
            <label htmlFor={id} style={fieldLabelStyle}>{label}</label>
            <InputTextarea
                id={id}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={rows}
                style={{ width: "100%" }}
            />
        </div>
    );

    const renderStandardField = (fieldConfig: StandardDelegationFieldConfig) => {
        const cols = resolveStandardFieldCols(fieldConfig);
        const fieldValue = fieldConfig.field === "stepsToPerformTask"
            ? formatDelegationTaskSteps(formData[fieldConfig.field])
            : formData[fieldConfig.field];
        const inputId = `${activeFormCode}-${fieldConfig.field}`;
        const isFullWidthDelegatedMedicationDetails = activeFormCode === "13-678" && fieldConfig.field === "delegatedMedicationDetails";

        if (fieldConfig.kind === "textarea") {
            return (
                <div
                    className={cols}
                    key={fieldConfig.field}
                    style={isFullWidthDelegatedMedicationDetails ? { width: "100%", maxWidth: "100%", flex: "0 0 100%" } : undefined}
                >
                    {renderTextareaField(
                        inputId,
                        fieldConfig.label,
                        fieldValue || "",
                        fieldConfig.rows || 4,
                        (nextValue) => updateField(fieldConfig.field, nextValue),
                    )}
                </div>
            );
        }

        if (fieldConfig.kind === "date") {
            return (
                <div className={cols} key={fieldConfig.field}>
                    {renderDateField(
                        inputId,
                        fieldConfig.label,
                        fieldValue || "",
                        (nextValue) => updateField(fieldConfig.field, nextValue),
                    )}
                </div>
            );
        }

        if (fieldConfig.kind === "checkbox") {
            if (fieldConfig.field === "additionalTeachingAidesAttached") {
                return (
                    <div className={cols} key={fieldConfig.field}>
                        <div className="flex align-items-center gap-2" style={{ minHeight: "2.75rem" }}>
                            <Checkbox
                                inputId={inputId}
                                checked={Boolean(fieldValue)}
                                onChange={(event) => updateField(fieldConfig.field, Boolean(event.checked))}
                            />
                            <label htmlFor={inputId} style={fieldLabelStyle}>{fieldConfig.label}</label>
                        </div>
                    </div>
                );
            }
            return (
                <div className={cols} key={fieldConfig.field}>
                    {renderBooleanToggle(
                        fieldConfig.label,
                        Boolean(fieldValue),
                        (checked) => updateField(fieldConfig.field, checked),
                    )}
                </div>
            );
        }

        if (fieldConfig.kind === "checkboxGroup") {
            const options = Array.isArray(fieldConfig.options) ? fieldConfig.options as string[] : [];
            const selectedValues = Array.isArray(fieldValue) ? fieldValue : [];
            if (isCompactDelegationForm) {
                return (
                    <div className={cols} key={fieldConfig.field}>
                        <div style={standardFieldShellStyle}>
                            <label htmlFor={inputId} style={fieldLabelStyle}>{fieldConfig.label}</label>
                            <MultiSelect
                                inputId={inputId}
                                value={selectedValues}
                                options={options}
                                onChange={(event) => updateField(fieldConfig.field, event.value || [])}
                                placeholder="Select options"
                                display="comma"
                                maxSelectedLabels={2}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                );
            }
            return (
                <div className={cols} key={fieldConfig.field}>
                    <div style={standardFieldShellStyle}>
                        <div style={fieldLabelStyle}>{fieldConfig.label}</div>
                        <div style={optionGroupStyle}>
                            {options.map((option) => (
                                <label key={option} style={standardOptionChipStyle}>
                                    <span style={optionCheckboxWrapStyle}>
                                        <Checkbox
                                            inputId={`${inputId}-${option}`}
                                            checked={selectedValues.includes(option)}
                                            onChange={() => {
                                                const next = selectedValues.includes(option)
                                                    ? selectedValues.filter((item: string) => item !== option)
                                                    : [...selectedValues, option];
                                                updateField(fieldConfig.field, next);
                                            }}
                                        />
                                    </span>
                                    <span style={{ lineHeight: 1.4 }}>{option}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        if (fieldConfig.kind === "radioGroup") {
            const options = Array.isArray(fieldConfig.options)
                ? (fieldConfig.options as Array<{ label: string; value: string } | string>).map((option) => (
                    typeof option === "string" ? { label: option, value: option } : option
                ))
                : [];
            const normalizedValue = typeof fieldValue === "string" ? fieldValue : "";
            if (isCompactDelegationForm) {
                return (
                    <div className={cols} key={fieldConfig.field}>
                        <div style={standardFieldShellStyle}>
                            <label htmlFor={inputId} style={fieldLabelStyle}>{fieldConfig.label}</label>
                            <Dropdown
                                inputId={inputId}
                                value={normalizedValue}
                                options={options}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(event) => updateField(fieldConfig.field, event.value || "")}
                                placeholder="Select an option"
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                );
            }
            return (
                <div className={cols} key={fieldConfig.field}>
                    {renderRadioGroup(
                        fieldConfig.label,
                        inputId,
                        normalizedValue,
                        options,
                        (value) => updateField(fieldConfig.field, value),
                    )}
                </div>
            );
        }

        return (
            <div className={cols} key={fieldConfig.field}>
                {renderTextField(inputId, fieldConfig.label, fieldValue || "", fieldConfig.field)}
            </div>
        );
    };

    const renderPrnMedicationOrders = () => {
        const prnOrders = Array.isArray(formData?.prnOrders) && formData.prnOrders.length > 0
            ? formData.prnOrders
            : [createEmptyPrnMedicationOrder()];

        return (
            <div className="col-12" ref={standardSectionRefs[1]}>
                <Card style={formCardStyle} className="referral-form-card">
                    {renderSectionHeader(
                        "PRN Medication Orders",
                        "Add one medication block for each delegated PRN medication. New assessment automation can now create as many PRN rows as needed.",
                    )}
                    <div className="flex justify-content-end mb-3">
                        <Button
                            type="button"
                            label="Add PRN Medication"
                            icon="pi pi-plus"
                            severity="secondary"
                            rounded
                            outlined
                            onClick={addPrnOrder}
                        />
                    </div>
                    <div className="flex flex-column gap-3">
                        {prnOrders.map((order: any, index: number) => (
                            <div
                                key={`prn-order-${index}`}
                                style={{
                                    border: "1px solid #dbe7f3",
                                    borderRadius: 18,
                                    padding: "1.15rem 1.2rem",
                                    background: "#ffffff",
                                    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)",
                                }}
                            >
                                <div className="flex justify-content-between align-items-center gap-3 mb-3">
                                    <div style={{ fontWeight: 800, color: "#0f172a" }}>
                                        PRN Medication {index + 1}
                                    </div>
                                    {prnOrders.length > 1 ? (
                                        <Button
                                            type="button"
                                            icon="pi pi-trash"
                                            severity="danger"
                                            rounded
                                            outlined
                                            size="small"
                                            onClick={() => removePrnOrder(index)}
                                        />
                                    ) : null}
                                </div>
                                <div className="grid form-section-grid">
                                    <div className="col-12 md:col-4">
                                        {renderDateField(
                                            `prnDateOrdered-${index}`,
                                            "Date Ordered",
                                            order?.dateOrdered || "",
                                            (nextValue) => updatePrnOrderField(index, "dateOrdered", nextValue),
                                            { width: "11rem" },
                                        )}
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <label htmlFor={`prnMedicationName-${index}`} style={fieldLabelStyle}>Medication Name</label>
                                        <InputText
                                            id={`prnMedicationName-${index}`}
                                            value={order?.medicationName || ""}
                                            onChange={(e) => updatePrnOrderField(index, "medicationName", e.target.value)}
                                            style={{ width: "100%" }}
                                        />
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <label htmlFor={`prnDoseFrequency-${index}`} style={fieldLabelStyle}>Dose / Frequency</label>
                                        <InputText
                                            id={`prnDoseFrequency-${index}`}
                                            value={order?.doseFrequency || ""}
                                            onChange={(e) => updatePrnOrderField(index, "doseFrequency", e.target.value)}
                                            style={{ width: "100%" }}
                                        />
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <label htmlFor={`prnRoute-${index}`} style={fieldLabelStyle}>Route</label>
                                        <InputText
                                            id={`prnRoute-${index}`}
                                            value={order?.route || ""}
                                            onChange={(e) => updatePrnOrderField(index, "route", e.target.value)}
                                            style={{ width: "100%" }}
                                        />
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <label htmlFor={`prnNotToExceed-${index}`} style={fieldLabelStyle}>Not to Exceed</label>
                                        <InputText
                                            id={`prnNotToExceed-${index}`}
                                            value={order?.notToExceed || ""}
                                            onChange={(e) => updatePrnOrderField(index, "notToExceed", e.target.value)}
                                            style={{ width: "100%" }}
                                        />
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <label htmlFor={`prnReason-${index}`} style={fieldLabelStyle}>Reason for Medication</label>
                                        <InputText
                                            id={`prnReason-${index}`}
                                            value={order?.reason || ""}
                                            onChange={(e) => updatePrnOrderField(index, "reason", e.target.value)}
                                            style={{ width: "100%" }}
                                        />
                                    </div>
                                    <div className="col-12 md:col-6">
                                        {renderTextareaField(
                                            `prnSymptomsAmount-${index}`,
                                            "Symptoms for Administration and Amount to Be Given",
                                            order?.symptomsAmount || "",
                                            3,
                                            (nextValue) => updatePrnOrderField(index, "symptomsAmount", nextValue),
                                        )}
                                    </div>
                                    <div className="col-12">
                                        {renderTextareaField(
                                            `prnNotes-${index}`,
                                            "Notes",
                                            order?.notes || "",
                                            3,
                                            (nextValue) => updatePrnOrderField(index, "notes", nextValue),
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        );
    };

    const renderRescindingDelegationRows = () => {
        const rescindedEntries = Array.isArray(formData?.rescindedEntries) && formData.rescindedEntries.length > 0
            ? formData.rescindedEntries
            : [createEmptyRescindingDelegationRow()];

        return (
            <div className="col-12" ref={standardSectionRefs[1]}>
                <Card style={formCardStyle} className="referral-form-card">
                    {renderSectionHeader(
                        "Caregivers, Medications, and Notes",
                        "Fill the rescinding table in the same caregiver, medication or treatment, and notes layout shown on the official form. The generated document supports up to 10 rows.",
                    )}
                    <div className="flex justify-content-between align-items-center gap-3 mb-3">
                        <div style={{ ...appMutedTextStyle, color: "#475569" }}>
                            {rescindedEntries.length} of {RESCINDING_DELEGATION_MAX_ROWS} table rows in use
                        </div>
                        <Button
                            type="button"
                            label="Add Row"
                            icon="pi pi-plus"
                            severity="secondary"
                            rounded
                            outlined
                            disabled={rescindedEntries.length >= RESCINDING_DELEGATION_MAX_ROWS}
                            onClick={addRescindingDelegationRow}
                        />
                    </div>
                    <div className="flex flex-column gap-3">
                        {rescindedEntries.map((entry: any, index: number) => (
                            <div
                                key={`rescinding-entry-${index}`}
                                style={{
                                    border: "1px solid #dbe7f3",
                                    borderRadius: 18,
                                    padding: "1rem",
                                    background: "#ffffff",
                                    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)",
                                }}
                            >
                                <div className="flex justify-content-between align-items-center gap-3 mb-3">
                                    <div style={{ fontWeight: 800, color: "#0f172a" }}>
                                        Table Row {index + 1}
                                    </div>
                                    {rescindedEntries.length > 1 ? (
                                        <Button
                                            type="button"
                                            icon="pi pi-trash"
                                            severity="danger"
                                            rounded
                                            outlined
                                            size="small"
                                            onClick={() => removeRescindingDelegationRow(index)}
                                        />
                                    ) : null}
                                </div>
                                <div className="grid form-section-grid">
                                    <div className="col-12 md:col-4">
                                        <label htmlFor={`rescindingCaregiver-${index}`} style={fieldLabelStyle}>Caregiver Name</label>
                                        <InputText
                                            id={`rescindingCaregiver-${index}`}
                                            value={entry?.caregiverName || ""}
                                            onChange={(e) => updateRescindingDelegationRowField(index, "caregiverName", e.target.value)}
                                            style={{ width: "100%" }}
                                        />
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <label htmlFor={`rescindingMedication-${index}`} style={fieldLabelStyle}>Medication or Treatment Rescinded</label>
                                        <InputText
                                            id={`rescindingMedication-${index}`}
                                            value={entry?.medicationOrTreatment || ""}
                                            onChange={(e) => updateRescindingDelegationRowField(index, "medicationOrTreatment", e.target.value)}
                                            style={{ width: "100%" }}
                                        />
                                    </div>
                                    <div className="col-12 md:col-4">
                                        {renderTextareaField(
                                            `rescindingNotes-${index}`,
                                            "Notes",
                                            entry?.notes || "",
                                            3,
                                            (nextValue) => updateRescindingDelegationRowField(index, "notes", nextValue),
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        );
    };

    const renderLtcwCompetencyRows = () => {
        const competencyEntries = Array.isArray(formData?.ltcwCompetencyEntries) && formData.ltcwCompetencyEntries.length > 0
            ? formData.ltcwCompetencyEntries
            : [createEmptyLtcwCompetencyEntry()];

        return (
            <div className="col-12" ref={standardSectionRefs[2]}>
                <Card style={formCardStyle} className="referral-form-card">
                    {renderSectionHeader(
                        "Long Term Care Worker (LTCW) Training / Competency",
                        "Add one row per LTCW and mark the competency methods, training dates, and active credential status exactly the way the updated nursing visit template expects.",
                    )}
                    <div className="flex justify-content-end mb-3">
                        <Button
                            type="button"
                            label="Add LTCW Row"
                            icon="pi pi-plus"
                            severity="secondary"
                            rounded
                            outlined
                            onClick={addLtcwCompetencyEntry}
                        />
                    </div>
                    <div className="flex flex-column gap-3">
                        {competencyEntries.map((entry: any, index: number) => {
                            const activeCredential = String(entry?.activeCredential || "yes").trim().toLowerCase() !== "no";

                            return (
                                <div
                                    key={`ltcw-competency-${index}`}
                                    style={{
                                        border: "1px solid #dbe7f3",
                                        borderRadius: 18,
                                        padding: "1rem",
                                        background: "#ffffff",
                                        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)",
                                    }}
                                >
                                    <div className="flex justify-content-between align-items-center gap-3 mb-3">
                                        <div style={{ fontWeight: 800, color: "#0f172a" }}>
                                            LTCW Row {index + 1}
                                        </div>
                                        {competencyEntries.length > 1 ? (
                                            <Button
                                                type="button"
                                                icon="pi pi-trash"
                                                severity="danger"
                                                rounded
                                                outlined
                                                size="small"
                                                onClick={() => removeLtcwCompetencyEntry(index)}
                                            />
                                        ) : null}
                                    </div>
                                    <div className="grid" style={{ margin: "0 -0.5rem", rowGap: "1rem" }}>
                                        <div className="col-12 md:col-4">
                                            <label htmlFor={`ltcwName-${index}`} style={fieldLabelStyle}>LTCW Evaluated</label>
                                            <InputText
                                                id={`ltcwName-${index}`}
                                                value={entry?.ltcwName || ""}
                                                onChange={(e) => updateLtcwCompetencyEntryField(index, "ltcwName", e.target.value)}
                                                style={{ width: "100%" }}
                                            />
                                        </div>
                                        <div className="col-12 md:col-4">
                                            <label htmlFor={`ltcwCompetencyMethods-${index}`} style={fieldLabelStyle}>Competency Method(s)</label>
                                            <MultiSelect
                                                inputId={`ltcwCompetencyMethods-${index}`}
                                                value={[
                                                    entry?.observationDemonstration ? "Observation or Demonstration" : null,
                                                    entry?.verbalDescription ? "Verbal Description" : null,
                                                    entry?.recordReview ? "Record Review" : null,
                                                ].filter(Boolean)}
                                                options={["Observation or Demonstration", "Verbal Description", "Record Review"]}
                                                onChange={(event) => updateLtcwCompetencyMethods(index, event.value || [])}
                                                placeholder="Select methods"
                                                display="comma"
                                                maxSelectedLabels={1}
                                                style={{ width: "100%" }}
                                            />
                                        </div>
                                        <div className="col-12 md:col-4" style={{ minWidth: 0 }}>
                                            {renderDateField(
                                                `ltcwTrainingNeeded-${index}`,
                                                "Training Needed Date",
                                                entry?.trainingNeededDate || "",
                                                (nextValue) => updateLtcwCompetencyEntryField(index, "trainingNeededDate", nextValue),
                                                { width: "100%" },
                                                true,
                                            )}
                                        </div>
                                        <div className="col-12 md:col-4" style={{ minWidth: 0 }}>
                                            {renderDateField(
                                                `ltcwTrainingCompleted-${index}`,
                                                "Training Completed Date",
                                                entry?.trainingCompletedDate || "",
                                                (nextValue) => updateLtcwCompetencyEntryField(index, "trainingCompletedDate", nextValue),
                                                { width: "100%" },
                                                true,
                                            )}
                                        </div>
                                        <div className="col-12 md:col-4" style={{ minWidth: 0 }}>
                                            <label htmlFor={`ltcwOtherSpecify-${index}`} style={fieldLabelStyle}>Other (specify)</label>
                                            <InputText
                                                id={`ltcwOtherSpecify-${index}`}
                                                value={entry?.otherSpecify || ""}
                                                onChange={(e) => updateLtcwCompetencyEntryField(index, "otherSpecify", e.target.value)}
                                                style={{ width: "100%" }}
                                            />
                                        </div>
                                        <div className="col-12 md:col-4" style={{ minWidth: 0 }}>
                                            <div style={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
                                                <span style={fieldLabelStyle}>Active Credential</span>
                                                <label className="flex align-items-center gap-2" htmlFor={`ltcwActiveCredential-${index}`} style={{ minHeight: "2.75rem" }}>
                                                    <Checkbox
                                                        inputId={`ltcwActiveCredential-${index}`}
                                                        checked={activeCredential}
                                                        onChange={(e) => updateLtcwCompetencyEntryField(index, "activeCredential", e.checked === true ? "yes" : "no")}
                                                    />
                                                    <span>Credential is active</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            </div>
        );
    };

    const standardSectionRefs = [caseRequestRef, clientInfoRef, receiptRef, responseRef];
    const standardSectionNavItems = (standardFormDefinition?.sections || []).map((section, index) => ({
        title: section.title,
        ref: standardSectionRefs[index] || responseRef,
    }));

    const sectionNavItems = isStructuredReferralForm
        ? [
            {
                title: "Case / Resource Manager Request",
                ref: caseRequestRef,
            },
            {
                title: "Client Information",
                ref: clientInfoRef,
            },
            {
                title: "Confirmation of Receipt",
                ref: receiptRef,
            },
            {
                title: "Delegating Nurse Response",
                ref: responseRef,
            },
        ]
        : (standardSectionNavItems.length
            ? standardSectionNavItems
            : [
                {
                    title: "Form Overview",
                    ref: caseRequestRef,
                },
            ]);

    const scrollToSection = (targetRef: { current: HTMLDivElement | null }) => {
        targetRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const requestDelegationDocument = async () => {
        if (!formId) {
            throw new Error("Save this delegation form first before generating the document.");
        }
        await backendAPIStore.init();
        return !usesProviderDelegationApi && userRoleType === ROLE_TYPE_NURSE
            ? await backendAPIStore.generateNurseDelegationDocument(formId)
            : await backendAPIStore.generateProviderDelegationDocument(formId);
    };

    const downloadDelegationDocument = () => {
        void generateDelegationDocument(false);
    };

    if (userRoleType !== ROLE_TYPE_NURSE && userRoleType !== ROLE_TYPE_MHP && userRoleType !== ROLE_TYPE_PROVIDER && !isAdminUser) {
        return <Unauthorized />;
    }

    const buildDelegationBinderDocumentFromPage = (root: HTMLElement, title: string) => {
        const clone = root.cloneNode(true) as HTMLElement;
        clone.querySelectorAll(".no-print").forEach((node) => node.remove());

        const originalControls = root.querySelectorAll("input, textarea, select");
        const clonedControls = clone.querySelectorAll("input, textarea, select");

        clonedControls.forEach((control, index) => {
            const original = originalControls[index] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | undefined;
            if (!original) {
                control.remove();
                return;
            }

            let replacement: HTMLElement;
            if (original instanceof HTMLInputElement && (original.type === "checkbox" || original.type === "radio")) {
                replacement = document.createElement("span");
                replacement.className = "binder-static-choice";
                replacement.textContent = original.type === "checkbox"
                    ? (original.checked ? "☑" : "☐")
                    : (original.checked ? "◉" : "○");
            } else {
                replacement = document.createElement("div");
                replacement.className = "binder-static-value";
                const rawValue = original instanceof HTMLSelectElement
                    ? original.options[original.selectedIndex]?.text || ""
                    : original.value || "";
                replacement.textContent = rawValue.trim() || "";
            }

            control.replaceWith(replacement);
        });
        return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
  <head>
    <meta charset="utf-8" />
    <meta name="ProgId" content="Word.Document" />
    <meta name="Generator" content="AFH Hub" />
    <meta name="Originator" content="AFH Hub" />
    <title>${title}</title>
    <style>
      ${floatLabelCss}
      @page {
        size: Letter portrait;
        margin: 0.45in;
      }
      html,
      body {
        width: 100%;
        max-width: 100%;
        overflow-x: hidden;
      }
      * {
        box-sizing: border-box;
        max-width: 100%;
      }
      .grid {
        display: flex;
        flex-wrap: wrap;
        margin-left: -0.6rem;
        margin-right: -0.6rem;
      }
      .grid > [class*="col-"] {
        padding: 0.6rem;
        box-sizing: border-box;
      }
      .col-12 {
        width: 100%;
      }
      .md\\:col-6 {
        width: 100%;
        max-width: 100%;
      }
      .md\\:col-4 {
        width: 100%;
        max-width: 100%;
      }
      .md\\:col-3 {
        width: 100%;
        max-width: 100%;
      }
      .flex {
        display: flex;
      }
      .flex-wrap {
        flex-wrap: wrap;
      }
      .flex-column {
        flex-direction: column;
      }
      .justify-content-between {
        justify-content: space-between;
      }
      .justify-content-end {
        justify-content: flex-end;
      }
      .justify-content-start {
        justify-content: flex-start;
      }
      .align-items-start {
        align-items: flex-start;
      }
      .align-items-center {
        align-items: center;
      }
      .gap-2 {
        gap: 0.5rem;
      }
      .gap-3 {
        gap: 0.75rem;
      }
      .mt-1 {
        margin-top: 0.25rem;
      }
      .mt-2 {
        margin-top: 0.5rem;
      }
      .mt-3 {
        margin-top: 1rem;
      }
      .mt-4 {
        margin-top: 1.5rem;
      }
      body {
        font-family: Arial, sans-serif;
        color: #111827;
        margin: 0;
        line-height: 1.45;
        background: #ffffff;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
      .delegation-referral-form {
        background: #ffffff !important;
        padding: 0 !important;
        width: 100%;
        max-width: 100%;
      }
      .print-shell {
        width: 100%;
        max-width: 7.6in;
        margin: 0 auto;
      }
      .referral-layout-shell {
        display: block;
        width: 100%;
        max-width: 100%;
      }
      .form-section-grid {
        row-gap: 0.45rem;
      }
      .current-working-version-summary,
      .print-note-card,
      .referral-form-card {
        width: 100%;
      }
      .p-card,
      .referral-form-card,
      .print-note-card,
      .current-working-version-summary {
        break-inside: avoid;
      }
      .p-card-body,
      .p-card-content {
        padding: 0;
      }
      .p-tag {
        display: inline-flex;
        align-items: center;
        border-radius: 999px;
        padding: 0.25rem 0.7rem;
        font-size: 0.78rem;
        font-weight: 700;
        background: #eef2ff;
        color: #1e3a8a;
      }
      .binder-static-value {
        width: 100%;
        min-height: 44px;
        padding: 0.75rem 0.85rem;
        border-radius: 10px;
        border: 1px solid #cbd5e1;
        background: #ffffff;
        color: #0f172a;
        white-space: pre-wrap;
        line-height: 1.5;
        margin-top: 0.2rem;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
      .binder-static-choice {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1rem;
        min-width: 1rem;
        color: #0f172a;
        font-weight: 700;
      }
      .p-float-label {
        display: block;
        width: 100%;
      }
      .p-float-label label {
        position: static !important;
        display: block;
        transform: none !important;
        margin-bottom: 0.45rem;
        color: #475569 !important;
        font-size: 0.92rem !important;
        font-weight: 700;
      }
      .option-group,
      .timeline-shell {
        display: block !important;
      }
      .option-chip,
      .timeline-button {
        display: block !important;
        width: 100% !important;
        margin-bottom: 0.65rem;
      }
      .timeline-button:last-child,
      .option-chip:last-child {
        margin-bottom: 0;
      }
      table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
      }
      * {
        color: inherit;
      }
    </style>
  </head>
  <body>
    <div class="delegation-referral-form">
      ${clone.innerHTML}
    </div>
  </body>
</html>`;
    };

    const saveDelegationToBinder = async (_fileContentOverride?: string) => {
        if (!residentId || !formConfig) {
            return;
        }

        setSavingToBinder(true);
        try {
            const response = await requestDelegationDocument();
            const outputKey = String(response?.outputKey || "").trim();
            const fileName = String(response?.fileName || "").trim();
            if (!outputKey || !fileName) {
                throw new Error("Delegation document is missing binder save details.");
            }

            const bundle = await ensureResidentBinderBundle(backendAPIStore, residentId);
            const targetFolder = getBinderFolderByCode(bundle.folders, RESIDENT_BINDER_FOLDER_CODES.NURSE_DELEGATION);
            if (!bundle.binder?.binderId || !targetFolder?.folderId) {
                throw new Error("Resident binder folder is not available.");
            }

            await upsertBinderDocumentWithVersion({
                backendAPIStore,
                binderId: bundle.binder.binderId,
                folderId: targetFolder.folderId,
                folderName: targetFolder.folderName || "Nurse Delegation Documents",
                referenceId: residentId,
                title: currentDisplayTitle,
                fileName,
                mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                existingDocuments: bundle.documents,
                existingObjectKey: outputKey,
            });

            toast.current?.show({
                severity: "success",
                summary: "Saved to Binder",
                detail: "The delegation document was saved to the resident binder.",
                life: 3500,
            });
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to save the delegation document to binder.",
                life: 4000,
            });
        } finally {
            setSavingToBinder(false);
        }
    };

    const generateDelegationDocument = async (saveToBinder: boolean) => {
        if (!residentId) return;
        if (saveToBinder) {
            await saveDelegationToBinder();
            return;
        }
        const response = await requestDelegationDocument();
        const documentUrl = String(response?.documentUrl || "").trim();
        if (!documentUrl) {
            throw new Error(response?.message || "Delegation document could not be generated.");
        }
        openUrlInNewTab(documentUrl);
        toast.current?.show({
            severity: "success",
            summary: "Download started",
            detail: "The delegation document is being downloaded.",
            life: 3500,
        });
    };

    const renderPrimaryDelegationActions = () => (
        <>
            <Button
                type="button"
                label="Save Draft"
                icon="pi pi-save"
                severity="warning"
                rounded
                outlined
                onClick={() => { void saveDraft(); }}
                loading={savingDraft}
                disabled={loadingForm}
            />
            <Button
                type="button"
                label="Mark As Complete"
                icon="pi pi-check-circle"
                severity="success"
                rounded
                outlined
                onClick={() => { void markAsComplete(); }}
                loading={savingDraft}
                disabled={loadingForm}
            />
        </>
    );

    const renderStandardDelegationFields = () => {
        const sections = standardFormDefinition?.sections || [];

        if (!sections.length) {
            return (
                <div className="col-12" ref={caseRequestRef}>
                    <Card style={formCardStyle} className="referral-form-card">
                        {renderSectionHeader(
                            "Delegation Form Workspace",
                            "This form does not have a custom layout yet. Use the official form link while we finish the structured workspace.",
                        )}
                        <div className="grid form-section-grid">
                            <div className="col-12">
                                {renderTextField("fallbackNotes", "Working Notes", formData.fallbackNotes || "", "fallbackNotes")}
                            </div>
                        </div>
                    </Card>
                </div>
            );
        }

        if (activeFormCode === PRN_MEDICATION_FORM_CODE) {
            const residentAndSettingSection = sections[0];
            const signOffSection = sections[2];

            return (
                <>
                    {residentAndSettingSection ? (
                        <div className="col-12" ref={standardSectionRefs[0]}>
                            <Card style={formCardStyle} className="referral-form-card">
                                {renderSectionHeader(residentAndSettingSection.title, residentAndSettingSection.subtitle)}
                                <div className="grid form-section-grid">
                                    {residentAndSettingSection.fields.map((fieldConfig) => renderStandardField(fieldConfig))}
                                </div>
                            </Card>
                        </div>
                    ) : null}

                    {renderPrnMedicationOrders()}

                    {signOffSection ? (
                        <div className="col-12" ref={standardSectionRefs[2]}>
                            <Card style={formCardStyle} className="referral-form-card">
                                {renderSectionHeader(signOffSection.title, signOffSection.subtitle)}
                                <div className="grid form-section-grid">
                                    {signOffSection.fields.map((fieldConfig) => renderStandardField(fieldConfig))}
                                </div>
                            </Card>
                        </div>
                    ) : null}
                </>
            );
        }

        if (activeFormCode === RESCINDING_DELEGATION_FORM_CODE) {
            const contextSection = sections[0];
            const followUpSection = sections[1];

            return (
                <>
                    {contextSection ? (
                        <div className="col-12" ref={standardSectionRefs[0]}>
                            <Card style={formCardStyle} className="referral-form-card">
                                {renderSectionHeader(contextSection.title, contextSection.subtitle)}
                                <div className="grid form-section-grid">
                                    {contextSection.fields.map((fieldConfig) => renderStandardField(fieldConfig))}
                                </div>
                            </Card>
                        </div>
                    ) : null}

                    {renderRescindingDelegationRows()}

                    {followUpSection ? (
                        <div className="col-12" ref={standardSectionRefs[2]}>
                            <Card style={formCardStyle} className="referral-form-card">
                                {renderSectionHeader(followUpSection.title, followUpSection.subtitle)}
                                <div className="grid form-section-grid">
                                    {followUpSection.fields.map((fieldConfig) => renderStandardField(fieldConfig))}
                                </div>
                            </Card>
                        </div>
                    ) : null}
                </>
            );
        }

        if (activeFormCode === NURSING_VISIT_FORM_CODE) {
            const visitOverviewSection = sections[0];
            const conditionReviewSection = sections[1];
            const followUpSection = sections[2];

            return (
                <>
                    {visitOverviewSection ? (
                        <div className="col-12" ref={standardSectionRefs[0]}>
                            <Card style={formCardStyle} className="referral-form-card">
                                {renderSectionHeader(visitOverviewSection.title, visitOverviewSection.subtitle)}
                                <div className="grid form-section-grid">
                                    {visitOverviewSection.fields.map((fieldConfig) => renderStandardField(fieldConfig))}
                                </div>
                            </Card>
                        </div>
                    ) : null}

                    {conditionReviewSection ? (
                        <div className="col-12" ref={standardSectionRefs[1]}>
                            <Card style={formCardStyle} className="referral-form-card">
                                {renderSectionHeader(conditionReviewSection.title, conditionReviewSection.subtitle)}
                                <div className="grid form-section-grid">
                                    {conditionReviewSection.fields.map((fieldConfig) => renderStandardField(fieldConfig))}
                                </div>
                            </Card>
                        </div>
                    ) : null}

                    {renderLtcwCompetencyRows()}

                    {followUpSection ? (
                        <div className="col-12" ref={responseRef}>
                            <Card style={formCardStyle} className="referral-form-card">
                                {renderSectionHeader(followUpSection.title, followUpSection.subtitle)}
                                <div className="grid form-section-grid">
                                    {followUpSection.fields.map((fieldConfig) => renderStandardField(fieldConfig))}
                                </div>
                            </Card>
                        </div>
                    ) : null}
                </>
            );
        }

        if (isCompactDelegationForm) {
            return (
                <div className="col-12" ref={standardSectionRefs[0]}>
                    <div className="grid form-section-grid">
                        {sections.flatMap((section) => [
                            <div className="col-12" key={`${section.title}-heading`} style={compactSectionHeadingStyle}>{section.title}</div>,
                            ...section.fields.map((fieldConfig) => renderStandardField(fieldConfig)),
                        ])}
                    </div>
                </div>
            );
        }

        return (
            <>
                {sections.map((section, index) => (
                    <div className="col-12" ref={standardSectionRefs[index]} key={`${section.title}-${index}`}>
                        <Card style={formCardStyle} className="referral-form-card">
                            {renderSectionHeader(section.title, section.subtitle)}
                            <div className="grid form-section-grid">
                                {section.fields.map((fieldConfig) => renderStandardField(fieldConfig))}
                            </div>
                        </Card>
                    </div>
                ))}
            </>
        );
    };

    return (
        <div style={pageShellStyle} className="delegation-referral-form">
            <style>{floatLabelCss}</style>
            <Toast ref={toast} />
            <Dialog
                visible={showRenameDialog}
                onHide={() => {
                    if (!savingThreadLabel) {
                        setShowRenameDialog(false);
                    }
                }}
                header="Edit File Name"
                style={{ width: "92vw", maxWidth: "30rem" }}
            >
                <div style={{ color: "#475569", lineHeight: 1.7 }}>
                    Update the saved file name for this delegation form.
                </div>
                <div className="mt-3">
                    <label htmlFor="delegation-thread-label" style={fieldLabelStyle}>File name suffix</label>
                    <div
                        style={{
                            border: "1px solid #dbe7f3",
                            background: "#f8fafc",
                            borderRadius: 12,
                            padding: "0.75rem 0.85rem",
                            color: "#334155",
                            fontWeight: 700,
                            marginBottom: "0.75rem",
                            lineHeight: 1.5,
                        }}
                    >
                        {renameTitlePrefix}
                    </div>
                    <InputText
                        id="delegation-thread-label"
                        value={pendingThreadLabel}
                        onChange={(e) => setPendingThreadLabel(e.target.value)}
                        placeholder="Example: May 2026 renewal"
                        style={{ width: "100%" }}
                        autoFocus
                    />
                    <div className="mt-2" style={appMutedTextStyle}>
                        Preview: {renamePreviewTitle}
                    </div>
                </div>
                <div className="mt-4 flex justify-content-end gap-2">
                    <Button
                        type="button"
                        label="Cancel"
                        severity="secondary"
                        outlined
                        onClick={() => setShowRenameDialog(false)}
                        disabled={savingThreadLabel}
                    />
                    <Button
                        type="button"
                        label="Save"
                        icon="pi pi-check"
                        onClick={() => { void saveThreadLabel(); }}
                        loading={savingThreadLabel}
                    />
                </div>
            </Dialog>
            <Dialog
                visible={showFeatureAccessDialog}
                onHide={() => {
                    featureAccessRetryActionRef.current = null;
                    setShowFeatureAccessDialog(false);
                }}
                header="Nurse Subscription Required"
                style={{ width: "92vw", maxWidth: "34rem" }}
            >
                <div style={{ color: "#475569", lineHeight: 1.7 }}>
                    {featureAccessDialogMessage}
                </div>
                {!requiresProviderSubscriptionMessageOnly ? (
                <div className="mt-3" style={{ color: "#64748b", fontSize: "0.92rem" }}>
                    Nurse subscription includes unlimited delegation forms, so once you subscribe you can keep saving drafts and completed forms for residents.
                </div>
                ) : null}
                {!requiresProviderSubscriptionMessageOnly && isNurseUser ? (
                    <div
                        className="mt-3"
                        style={{
                            border: "1px solid #bfdbfe",
                            background: "linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)",
                            borderRadius: 16,
                            padding: "1rem 1.1rem",
                        }}
                    >
                        <div style={{ color: "#1d4ed8", fontWeight: 800, marginBottom: "0.35rem", fontSize: "1rem" }}>
                            One-time 7-day access
                        </div>
                        <div style={{ color: "#1e3a8a", lineHeight: 1.6 }}>
                            {featureAccessUnlockEligible
                                ? "Use your one-time 7-day nurse access unlock to keep saving delegation forms before you subscribe."
                                : featureAccessUnlockExpiresAt
                                    ? `Your one-time nurse unlock was already used. It was active until ${formattedFeatureAccessUnlockExpiryDate}.`
                                    : "Your one-time 7-day nurse access unlock has already been used."}
                        </div>
                        {featureAccessUnlockError ? (
                            <div style={{ color: "#b91c1c", marginTop: "0.65rem", lineHeight: 1.5 }}>
                                {featureAccessUnlockError}
                            </div>
                        ) : null}
                    </div>
                ) : null}
                {!requiresProviderSubscriptionMessageOnly ? (
                    <div className="mt-4 flex flex-wrap gap-2 justify-content-end">
                        <Button
                            type="button"
                            label="Not Now"
                            severity="secondary"
                            outlined
                            onClick={() => {
                                featureAccessRetryActionRef.current = null;
                                setShowFeatureAccessDialog(false);
                            }}
                        />
                        {isNurseUser && featureAccessUnlockEligible ? (
                            <Button
                                type="button"
                                label={loadingFeatureAccessUnlockState ? "Checking..." : "Unlock Access for 7 Days"}
                                severity="info"
                                rounded
                                outlined
                                loading={activatingFeatureAccessUnlock}
                                disabled={loadingFeatureAccessUnlockState || activatingFeatureAccessUnlock}
                                onClick={() => { void handleActivateFeatureAccessUnlock(); }}
                            />
                        ) : null}
                        <Button
                            type="button"
                            label="Subscription Options"
                            severity="warning"
                            onClick={() => {
                                featureAccessRetryActionRef.current = null;
                                setShowFeatureAccessDialog(false);
                                navigate(`${RouterAddress.BILLING}?tab=subscription`);
                            }}
                        />
                    </div>
                ) : null}
            </Dialog>
            <div ref={printShellRef} style={dashboardPageContainerStyle} className="print-shell">
                <div
                    className="flex justify-content-between align-items-start gap-3 flex-wrap mb-4 no-print"
                    style={{ width: "100%" }}
                >
                    <div style={{ minWidth: 0, flex: "1 1 22rem" }}>
                        <div className="flex flex-wrap align-items-center gap-2">
                            <div className="m-0" style={{ fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.2, color: "#0f172a" }}>
                                {currentDisplayTitle}
                            </div>
                            <Button
                                type="button"
                                icon="pi pi-pencil"
                                rounded
                                text
                                severity="warning"
                                aria-label="Rename file"
                                tooltip="Rename file"
                                tooltipOptions={{ position: "top" }}
                                onClick={openRenameDialog}
                                disabled={savingThreadLabel}
                                style={{ color: "#f59e0b" }}
                            />
                        </div>
                        {!isCompactDelegationForm ? (
                            <div
                                className="mt-2"
                                style={{
                                    color: "#596F80",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {`Resident Name: ${`${residentProfile?.firstName || ""} ${residentProfile?.lastName || ""}`.trim() || "Resident"}`}
                            </div>
                        ) : null}
                    </div>
                    <div
                        data-tour="nurse-delegation-actions"
                        className="flex flex-wrap justify-content-end gap-2 align-items-center"
                        style={{ flex: "0 1 auto" }}
                    >
                        <Button
                            type="button"
                            label="Download"
                            severity="warning"
                            rounded
                            outlined
                            icon="pi pi-download"
                            onClick={downloadDelegationDocument}
                            disabled={loadingForm || savingDraft || !residentId}
                        />
                        {canSaveDelegationToBinder ? (
                            <Button
                                type="button"
                                label="Add to Binder"
                                icon="pi pi-bookmark"
                                severity="warning"
                                rounded
                                outlined
                                onClick={() => { void saveDelegationToBinder(); }}
                                loading={savingToBinder}
                                disabled={loadingForm || savingDraft || !residentId}
                            />
                        ) : null}
                        {renderPrimaryDelegationActions()}
                    </div>
                </div>

                <div style={centeredContentStyle}>
                    {loadingForm ? (
                        <div className="flex justify-content-center" style={{ padding: "2rem" }}>
                            <ProgressSpinner />
                        </div>
                    ) : (
                        <>
                            {!!formInstructions.length && !isCompactDelegationForm && (
                                    <Card
                                        style={{ ...appSectionCardStyle, marginBottom: "1rem", padding: "0.95rem 1.05rem" }}
                                        className="print-note-card"
                                    >
                                        <div className="flex justify-content-between align-items-center gap-3 flex-wrap" style={{ marginBottom: showInstructions ? "0.45rem" : 0 }}>
                                            <div style={{ fontWeight: 800, color: "#0f172a" }}>
                                                {formInstructionsTitle || "Form Instructions"}
                                            </div>
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => setShowInstructions((prev) => !prev)}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter" || event.key === " ") {
                                                        event.preventDefault();
                                                        setShowInstructions((prev) => !prev);
                                                    }
                                                }}
                                                style={{ color: "#2563eb", fontWeight: 700, cursor: "pointer", userSelect: "none" }}
                                            >
                                                {showInstructions ? "Hide" : "Show"}
                                            </span>
                                        </div>
                                        {showInstructions ? (
                                            <div style={{ ...appMutedTextStyle, color: "#334155", lineHeight: 1.6 }}>
                                                {formInstructions.map((instruction) => (
                                                    <div key={instruction} style={{ marginTop: "0.3rem" }}>
                                                        {instruction}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                    </Card>
                            )}

                            <div style={standardFormBodyStyle} className="referral-layout-shell">
                                {isCompactDelegationForm && formInstructions.length ? (
                                    <>
                                        <div className="flex justify-content-end" style={{ marginBottom: showInstructions ? "0.35rem" : 0 }}>
                                            <Button
                                                type="button"
                                                icon="pi pi-info-circle"
                                                rounded
                                                text
                                                severity="secondary"
                                                aria-label={showInstructions ? "Hide form guidance" : "Show form guidance"}
                                                tooltip={showInstructions ? "Hide form guidance" : "View form guidance"}
                                                tooltipOptions={{ position: "left" }}
                                                onClick={() => setShowInstructions((prev) => !prev)}
                                            />
                                        </div>
                                        {showInstructions ? (
                                            <div
                                                className="print-note-card mb-3"
                                                style={{
                                                    borderBottom: "1px solid #dbe7f3",
                                                    paddingBottom: "0.75rem",
                                                    color: "#334155",
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: "0.35rem" }}>
                                                    {formInstructionsTitle || "Form Instructions"}
                                                </div>
                                                {formInstructions.map((instruction) => (
                                                    <div key={instruction} style={{ marginTop: "0.25rem" }}>
                                                        {instruction}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                    </>
                                ) : null}
                                {!isCompactDelegationForm ? (
                                    <div style={timelineNavStyle} className="referral-timeline-nav no-print">
                                        <div style={timelineTitleStyle}>Form Sections</div>
                                        <div style={timelineListStyle} className="timeline-shell">
                                            {sectionNavItems.map((item) => (
                                                <button
                                                    key={item.title}
                                                    type="button"
                                                    style={timelineButtonStyle}
                                                    className="timeline-button"
                                                    onClick={() => scrollToSection(item.ref)}
                                                >
                                                    <span className="timeline-dot" />
                                                    <span>
                                                        <div className="timeline-step-label">{item.title}</div>
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}

                                {isStructuredReferralForm ? (
                                    <div className="grid" style={{ alignItems: "stretch" }}>
                                        <div className="col-12" ref={caseRequestRef}>
                                            <Card style={formCardStyle} className="referral-form-card">
                                                {renderSectionHeader(
                                                    "Case / Resource Manager Request",
                                                    "Document the requesting office, referral method, nurse agency details, and required supporting attachments.",
                                                )}
                                                <div className="grid form-section-grid">
                                                    <div className="col-12 lg:col-7">{renderCheckGroup("Office", "offices", OFFICE_OPTIONS)}</div>
                                                    <div className="col-12 md:col-6 lg:col-5">
                                                        <span className="p-float-label">
                                                            <InputText id="officeOther" value={formData.officeOther} onChange={(e) => updateField("officeOther", e.target.value)} style={{ width: "100%" }} />
                                                            <label htmlFor="officeOther">Other</label>
                                                        </span>
                                                    </div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="authorizationNumber" value={formData.authorizationNumber} onChange={(e) => updateField("authorizationNumber", e.target.value)} style={{ width: "100%" }} /><label htmlFor="authorizationNumber">Authorization Number for Nurse Delegation</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="rnProviderOneId" value={formData.rnProviderOneId} onChange={(e) => updateField("rnProviderOneId", e.target.value)} style={{ width: "100%" }} /><label htmlFor="rnProviderOneId">RN ProviderOne ID</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4">{renderDateField("dateOfReferral", "Date of Referral", formData.dateOfReferral, (nextValue) => updateField("dateOfReferral", nextValue))}</div>
                                                    <div className="col-12 lg:col-7">{renderCheckGroup("Method of Referral", "methods", METHOD_OPTIONS)}</div>
                                                    <div className="col-12"><span className="p-float-label"><InputText id="nurseAgency" value={formData.nurseAgency} onChange={(e) => updateField("nurseAgency", e.target.value)} style={{ width: "100%" }} /><label htmlFor="nurseAgency">Nurse Delegator / Agency</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="nursePhone" value={formData.nursePhone} onChange={(e) => updateField("nursePhone", e.target.value)} style={{ width: "100%" }} /><label htmlFor="nursePhone">Telephone Number</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="nurseFax" value={formData.nurseFax} onChange={(e) => updateField("nurseFax", e.target.value)} style={{ width: "100%" }} /><label htmlFor="nurseFax">Fax Number</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="nurseEmail" value={formData.nurseEmail} onChange={(e) => updateField("nurseEmail", e.target.value)} style={{ width: "100%" }} /><label htmlFor="nurseEmail">Email Address</label></span></div>
                                                    <div className="col-12"><span className="p-float-label"><InputText id="crmNameOffice" value={formData.crmNameOffice} onChange={(e) => updateField("crmNameOffice", e.target.value)} style={{ width: "100%" }} /><label htmlFor="crmNameOffice">C/RM Name / Office</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="crmEmail" value={formData.crmEmail} onChange={(e) => updateField("crmEmail", e.target.value)} style={{ width: "100%" }} /><label htmlFor="crmEmail">Email Address</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="crmPhone" value={formData.crmPhone} onChange={(e) => updateField("crmPhone", e.target.value)} style={{ width: "100%" }} /><label htmlFor="crmPhone">Telephone Number</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="crmFax" value={formData.crmFax} onChange={(e) => updateField("crmFax", e.target.value)} style={{ width: "100%" }} /><label htmlFor="crmFax">Fax Number</label></span></div>
                                                    <div className="col-12 lg:col-9">{renderCheckGroup("Required Attachments", "attachments", ATTACHMENT_OPTIONS)}</div>
                                                </div>
                                            </Card>
                                        </div>

                                        <div className="col-12" ref={clientInfoRef}>
                                            <Card style={formCardStyle} className="referral-form-card">
                                                {renderSectionHeader(
                                                    "Client Information",
                                                    "Capture the resident, guardian, communication, provider, and diagnosis details needed to support delegation review.",
                                                )}
                                                <div className="grid form-section-grid">
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="clientName" value={formData.clientName} onChange={(e) => updateField("clientName", e.target.value)} style={{ width: "100%" }} /><label htmlFor="clientName">Client Name</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="guardianName" value={formData.guardianName} onChange={(e) => updateField("guardianName", e.target.value)} style={{ width: "100%" }} /><label htmlFor="guardianName">Guardian Name</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="acesId" value={formData.acesId} onChange={(e) => updateField("acesId", e.target.value)} style={{ width: "100%" }} /><label htmlFor="acesId">ACES ID</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4">{renderDateField("clientDob", "Client Date of Birth", formData.clientDob, (nextValue) => updateField("clientDob", nextValue))}</div>
                                                    <div className="col-12 md:col-6 lg:col-4">{renderTextField("clientPhone", "Client / Guardian Phone", formData.clientPhone, "clientPhone")}</div>
                                                    <div className="col-12 md:col-6 lg:col-4">{renderTextField("clientGuardianEmail", "Client / Guardian Email Address", formData.clientGuardianEmail, "clientGuardianEmail")}</div>
                                                    <div className="col-12"><span className="p-float-label"><InputText id="addressLine1" value={formData.addressLine1} onChange={(e) => updateField("addressLine1", e.target.value)} style={{ width: "100%" }} /><label htmlFor="addressLine1">Address</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="city" value={formData.city} onChange={(e) => updateField("city", e.target.value)} style={{ width: "100%" }} /><label htmlFor="city">City</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="stateZip" value={formData.stateZip} onChange={(e) => updateField("stateZip", e.target.value)} style={{ width: "100%" }} /><label htmlFor="stateZip">State / Zip Code</label></span></div>
                                                    <div className="col-12"><span className="p-float-label"><InputText id="providerName" value={formData.providerName} onChange={(e) => updateField("providerName", e.target.value)} style={{ width: "100%" }} /><label htmlFor="providerName">Long Term Care Worker(s) and/or Residential Provider&apos;s Name</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="providerPhone" value={formData.providerPhone} onChange={(e) => updateField("providerPhone", e.target.value)} style={{ width: "100%" }} /><label htmlFor="providerPhone">Telephone Number</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="providerFax" value={formData.providerFax} onChange={(e) => updateField("providerFax", e.target.value)} style={{ width: "100%" }} /><label htmlFor="providerFax">Fax Number</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-8">
                                                        <div
                                                            style={{
                                                                display: "grid",
                                                                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                                                                gap: "0.75rem",
                                                                alignItems: "start",
                                                            }}
                                                        >
                                                            {renderBooleanToggle("This client needs an interpreter", formData.needsInterpreter, (checked) => updateField("needsInterpreter", checked))}
                                                            {renderBooleanToggle("Primary Language Needed", Boolean(formData.primaryLanguage), (checked) => updateField("primaryLanguage", checked ? "Yes" : ""))}
                                                            {renderBooleanToggle("Deaf / HOH", formData.deafHoh, (checked) => updateField("deafHoh", checked))}
                                                        </div>
                                                    </div>
                                                    <div className="col-12 lg:col-8"><span className="p-float-label"><InputText id="primaryDiagnosis" value={formData.primaryDiagnosis} onChange={(e) => updateField("primaryDiagnosis", e.target.value)} style={{ width: "100%" }} /><label htmlFor="primaryDiagnosis">Primary Diagnosis Related to Delegation</label></span></div>
                                                    <div className="col-12 md:col-8 lg:col-8"><span className="p-float-label"><InputText id="crmSignature" value={formData.crmSignature} onChange={(e) => updateField("crmSignature", e.target.value)} style={{ width: "100%" }} /><label htmlFor="crmSignature">Case / Resource Manager&apos;s Signature</label></span></div>
                                                    <div className="col-12 md:col-4 lg:col-4">{renderDateField("crmSignatureDate", "Date", formData.crmSignatureDate, (nextValue) => updateField("crmSignatureDate", nextValue))}</div>
                                                    <div className="col-12"><span className="p-float-label"><InputTextarea id="referralReason" value={formData.referralReason} onChange={(e) => updateField("referralReason", e.target.value)} rows={4} style={{ width: "100%" }} /><label htmlFor="referralReason">Reason for RND Referral</label></span></div>
                                                </div>
                                            </Card>
                                        </div>

                                        <div className="col-12" ref={receiptRef}>
                                            <Card style={{ ...formCardStyle, height: "100%" }} className="referral-form-card">
                                                {renderSectionHeader(
                                                    "Confirmation of Receipt",
                                                    "Confirm the referral was received, record the assignment, and note any immediate follow-up details.",
                                                )}
                                                <div className="grid form-section-grid">
                                                    <div className="col-12 md:col-6 lg:col-4">{renderDateField("receiptDate", "Date Received", formData.receiptDate, (nextValue) => updateField("receiptDate", nextValue))}</div>
                                                    <div className="col-12 lg:col-8">
                                                        {renderRadioGroup(
                                                            "Referral Response",
                                                            "referralAccepted",
                                                            formData.referralAccepted,
                                                            [
                                                                { label: "Accepted", value: "accepted" },
                                                                { label: "Not Accepted", value: "notAccepted" },
                                                            ],
                                                            (value) => updateField("referralAccepted", value),
                                                        )}
                                                    </div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="assignedNurse" value={formData.assignedNurse} onChange={(e) => updateField("assignedNurse", e.target.value)} style={{ width: "100%" }} /><label htmlFor="assignedNurse">Nurse Assigned</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4"><span className="p-float-label"><InputText id="receiptSignature" value={formData.receiptSignature} onChange={(e) => updateField("receiptSignature", e.target.value)} style={{ width: "100%" }} /><label htmlFor="receiptSignature">Printed Name</label></span></div>
                                                    <div className="col-12 md:col-6 lg:col-4">{renderTextField("receiptContact", "Telephone / Email Address", formData.receiptContact, "receiptContact")}</div>
                                                    <div className="col-12">{renderTextareaField("additionalComments", "Additional Comments", formData.additionalComments, 3, (nextValue) => updateField("additionalComments", nextValue))}</div>
                                                </div>
                                            </Card>
                                        </div>

                                        <div className="col-12" ref={responseRef}>
                                            <Card style={{ ...formCardStyle, height: "100%" }} className="referral-form-card">
                                                {renderSectionHeader(
                                                    "Delegating Nurse Response",
                                                    "Record the nurse response, delegation status, delegated tasks, and final sign-off information.",
                                                )}
                                                <div className="grid form-section-grid">
                                                    <div className="col-12 md:col-6 lg:col-4">{renderTextField("responseCrmName", "C/RM Name", formData.responseCrmName, "responseCrmName")}</div>
                                                    <div className="col-12 md:col-6 lg:col-4">{renderTextField("responseCrmEmail", "Email Address", formData.responseCrmEmail, "responseCrmEmail")}</div>
                                                    <div className="col-12 md:col-6 lg:col-4">{renderTextField("responseCrmPhone", "Telephone Number", formData.responseCrmPhone, "responseCrmPhone")}</div>
                                                    <div className="col-12 md:col-6 lg:col-4">{renderTextField("rndName", "RND Name", formData.rndName, "rndName")}</div>
                                                    <div className="col-12 md:col-6 lg:col-4">{renderTextField("rndProviderOneId", "ProviderOne ID", formData.rndProviderOneId, "rndProviderOneId")}</div>
                                                    <div className="col-12 md:col-6 lg:col-4">{renderTextField("rndEmail", "Email Address", formData.rndEmail, "rndEmail")}</div>
                                                    <div className="col-12 md:col-6 lg:col-4">{renderTextField("rndPhone", "Telephone Number", formData.rndPhone, "rndPhone")}</div>
                                                    <div className="col-12 md:col-6 lg:col-4">{renderTextField("rndFax", "Fax Number", formData.rndFax, "rndFax")}</div>
                                                    <div className="col-12 lg:col-8">
                                                        {renderRadioGroup(
                                                            "Nurse delegation has started",
                                                            "delegationStarted",
                                                            formData.delegationStarted,
                                                            [
                                                                { label: "Yes", value: "yes" },
                                                                { label: "No", value: "no" },
                                                            ],
                                                            (value) => updateField("delegationStarted", value),
                                                        )}
                                                    </div>
                                                    <div className="col-12 md:col-6 lg:col-4">{renderDateField("assessmentDate", "Assessment Date", formData.assessmentDate, (nextValue) => updateField("assessmentDate", nextValue))}</div>
                                                    <div className="col-12"><span className="p-float-label"><InputTextarea id="delegatedTasks" value={formData.delegatedTasks} onChange={(e) => updateField("delegatedTasks", e.target.value)} rows={4} style={{ width: "100%" }} /><label htmlFor="delegatedTasks">List the Tasks that were Delegated</label></span></div>
                                                    <div className="col-12"><span className="p-float-label"><InputTextarea id="notImplementedReason" value={formData.notImplementedReason} onChange={(e) => updateField("notImplementedReason", e.target.value)} rows={3} style={{ width: "100%" }} /><label htmlFor="notImplementedReason">Nurse Delegation Was Not Implemented: Reason / Action Taken</label></span></div>
                                                    <div className="col-12"><span className="p-float-label"><InputTextarea id="careOptions" value={formData.careOptions} onChange={(e) => updateField("careOptions", e.target.value)} rows={3} style={{ width: "100%" }} /><label htmlFor="careOptions">RND Suggests These Other Options for Care</label></span></div>
                                                    <div className="col-12"><span className="p-float-label"><InputTextarea id="rndComments" value={formData.rndComments} onChange={(e) => updateField("rndComments", e.target.value)} rows={4} style={{ width: "100%" }} /><label htmlFor="rndComments">RND Additional Comments</label></span></div>
                                                    <div className="col-12 md:col-8"><span className="p-float-label"><InputText id="rndSignature" value={formData.rndSignature} onChange={(e) => updateField("rndSignature", e.target.value)} style={{ width: "100%" }} /><label htmlFor="rndSignature">Nurse Delegator&apos;s Signature</label></span></div>
                                                    <div className="col-12 md:col-4">{renderDateField("rndSignatureDate", "Date", formData.rndSignatureDate, (nextValue) => updateField("rndSignatureDate", nextValue))}</div>
                                                </div>
                                            </Card>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid" style={{ alignItems: "stretch" }}>
                                        {renderStandardDelegationFields()}
                                    </div>

                                )}
                                <div
                                    className="flex flex-wrap justify-content-end gap-2 align-items-center no-print"
                                    style={{ marginTop: "1rem" }}
                                >
                                    {renderPrimaryDelegationActions()}
                                </div>
                                {!isStructuredReferralForm && printFooter.length ? (
                                    <Card
                                        style={{ ...appSectionCardStyle, marginTop: "1rem", padding: "0.9rem 1rem" }}
                                        className="print-note-card"
                                    >
                                        <div style={{ ...appMutedTextStyle, color: "#334155", lineHeight: 1.55 }}>
                                            {printFooter.map((line) => (
                                                <div key={line}>{line}</div>
                                            ))}
                                        </div>
                                    </Card>
                                ) : null}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NurseDelegationReferralFormPage;
